// Section 24.14: all user-facing strings, keyed by id, including the narration templates used
// with api/narrate.ts. Templates use {param} placeholders filled by `fill`.
import type { GuardMandateOption, JusticeBlock, MemoryKind, PolicyId } from '../../sim/api';
import type {
  PriorityGroupId,
  RationPolicyId,
  RationResource,
  SeedReserveOption,
  WarmthOption,
  WaterRationOption,
} from '../../sim/api';
import type { EventTemplateId, MigrationOption } from '../../sim/api';
import type {
  BuildingTypeId,
  DeathCause,
  JobId,
  NarrationTemplateId,
  Narration,
  Resource,
  Season,
} from '../../sim/api';
import type {
  AgeGroup,
  ArrivalDecision,
  ArrivalReason,
  CrimeKind,
  EmigrationReason,
  ImprovementId,
  JusticeResponse,
  LedgerFate,
  LedgerTag,
  NormId,
  Origin,
  SketchLine,
  SketchTemplateId,
  SkillId,
  SteadyCondition,
  EventId as LedgerEventId,
} from '../../sim/api';

// ---- Sections 14 and 19 (stream E2): event headlines (phrased as observations), warnings,
// choice labels, what a choice did, one-line previews, the gate and departures.
const eventNarration: Record<EventTemplateId, string> = {
  'event.poor-harvest': 'The crops have not filled out; this harvest will be poor ({mul}x).',
  'event.blight': 'Blight has come into the fields. Burn the affected rows, or harvest anyway?',
  'event.blight.returns': 'The blight has come back into the fields.',
  'event.drought':
    'No rain. Water will be {water}x for {seasons} seasons, and forage and crops will suffer.',
  'event.harsh-winter':
    'A hard winter is setting in: more firewood will be needed, and more will fall ill.',
  'event.early-frost': 'An early frost has touched the fields; the harvest will be thinner.',
  'event.fire': 'Fire broke out in a {building}. {name} was hurt; {food} food was lost.',
  'event.fever-outbreak': 'Fever is spreading: several fell ill at once.',
  'event.plague': 'Plague has come to the settlement.',
  'event.wild-animals': 'A wild animal has been seen near the settlement.',
  'event.wild-animals.hurt': 'A wild animal attacked {name} while out working.',
  'event.wild-animals.child': 'A wild animal took {child}, who was left unwatched.',
  'event.wild-animals.hurt-child':
    'A wild animal attacked {name}, and took {child}, who was left unwatched.',
  'event.raid':
    'Raiders are at the edge of the fields. {guards} {guards|guard stands|guards stand} ready.',
  'event.refugees': '{size} refugees are at the gate.',
  'event.refugees.famine': '{size} people fleeing famine are at the gate.',
  'event.refugees.raiders': '{size} people fleeing raiders are at the gate.',
  'event.travellers': '{size} travellers seeking work are at the gate.',
  'event.trade-caravan':
    'A trade caravan offers {giveAmount1} {give1} for {getAmount1} {get1}, or {giveAmount2} {give2} for {getAmount2} {get2}.',
  'event.good-harvest': 'The crops stand tall; this harvest will be good ({mul}x).',
  'event.mild-winter': 'The winter is mild: less firewood will be needed, and fewer will fall ill.',
  'event.discovery': 'Foragers found something worth a closer look.',
  'event.discovery.herb-patch': 'Foragers found a patch of healing herbs.',
  'event.discovery.fish-run': 'Foragers found a run of fish in the stream.',
  'event.discovery.clay-bed': 'Foragers found a clay bed around a spring: room for another well.',
  'event.improvement-proposal': '{name} has an idea for the settlement.',
  'event.improvement-proposal.crop-rotation':
    '{name} proposes rotating the crops: harvests 10% better, for good.',
  'event.improvement-proposal.better-kiln':
    '{name} proposes a better kiln: building will cost less, for good.',
  'event.improvement-proposal.granary-lining':
    '{name} proposes lining the granary: stored food would spoil 1% instead of 3%.',
  'event.dispute': '{a} and {b} are in open conflict and look to you to decide.',
  'event.elders-request': '{name} has a request.',
  'event.elders-request.stop-working': '{name} asks to stop working.',
  'event.elders-request.teach': '{name} asks to teach.',
  'event.elders-request.leave': '{name} asks to leave and live with kin elsewhere.',
  'event.birth-complication': 'The birth of {child} to {mother} was hard; both are in danger.',
  'event.stranger': 'A stranger is at the gate, saying they are a healer.',
  'event.rumour':
    'There is talk of a better valley: {households} households are thinking of leaving.',
  'event.gate.followup': 'The guests have stayed their two seasons. Do they stay for good?',
  'foreshadow.poor-harvest': 'The crops look thin.',
  'foreshadow.drought': 'The well is lower than last Summer.',
  'foreshadow.harsh-winter': 'The geese went south early, and the squirrels are fat.',
  'foreshadow.fever-outbreak': 'A cough is going round the youngest.',
  'foreshadow.raid': 'Strangers were seen watching from the ridge.',
  'choice.blight.burn-fields': 'Burn the affected fields',
  'choice.blight.harvest-anyway': 'Harvest anyway',
  'choice.drought.dig-deeper': 'Dig deeper',
  'choice.drought.ration': 'Ration the water',
  'choice.fever-outbreak.quarantine': 'Quarantine the sick',
  'choice.fever-outbreak.carry-on': 'Carry on',
  'choice.plague.quarantine': 'Quarantine the sick',
  'choice.plague.carry-on': 'Carry on',
  'choice.plague.close-gates': 'Close the gates',
  'choice.wild-animals.hunt-it': 'Hunt it',
  'choice.wild-animals.avoid': 'Avoid it',
  'choice.raid.fight': 'Fight',
  'choice.raid.pay': 'Pay them',
  'choice.raid.hide': 'Hide',
  'choice.refugees.admit-all': 'Admit all',
  'choice.refugees.admit-some': 'Admit some',
  'choice.refugees.guests': 'Admit as guests',
  'choice.refugees.turn-away': 'Turn away',
  'choice.travellers.admit-all': 'Admit them',
  'choice.travellers.admit-some': 'Admit some',
  'choice.travellers.guests': 'Admit as guests',
  'choice.travellers.turn-away': 'Turn them away',
  'choice.trade-caravan.trade-first': 'Take the first offer',
  'choice.trade-caravan.trade-second': 'Take the second offer',
  'choice.trade-caravan.decline': 'Decline',
  'choice.discovery.develop': 'Send two foragers to develop it',
  'choice.discovery.ignore': 'Ignore it',
  'choice.improvement-proposal.commit': 'Commit a skilled worker for a season',
  'choice.improvement-proposal.decline': 'Decline',
  'choice.dispute.side-a': 'Side with {a}',
  'choice.dispute.side-b': 'Side with {b}',
  'choice.dispute.mediate': 'Mediate',
  'choice.elders-request.grant': 'Grant the request',
  'choice.elders-request.refuse': 'Refuse',
  'choice.birth-complication.assign-healer': 'Assign a healer now',
  'choice.birth-complication.do-nothing': 'Do nothing',
  'choice.stranger.admit': 'Admit the stranger',
  'choice.stranger.refuse': 'Refuse',
  'choice.rumour.address': 'Address it',
  'choice.rumour.let-go': 'Let them go',
  'chose.blight.burn-fields':
    'The blighted rows were burned: {loss}% of the crop is lost, but the soil will be clean.',
  'chose.blight.harvest-anyway':
    'The blighted crop will be harvested: the harvest is {mul}x and its seed is worth half.',
  'chose.drought.dig-deeper': '{builders} builders are digging the wells deeper this season.',
  'chose.drought.dig-deeper.none': 'No builders were at work, so nobody dug.',
  'chose.drought.ration': 'The water will be rationed through the drought.',
  'chose.fever-outbreak.quarantine': 'The sick are held apart for {seasons} seasons.',
  'chose.fever-outbreak.carry-on': 'Life goes on; the sick stay among the well.',
  'chose.plague.quarantine': 'The sick are held apart for {seasons} seasons.',
  'chose.plague.carry-on': 'Life goes on; the sick stay among the well.',
  'chose.plague.close-gates': 'The gates are closed to newcomers for {seasons} seasons.',
  'chose.wild-animals.hunt-it': 'The animal was hunted down: {food} food brought in.',
  'chose.wild-animals.hunt-it.none': 'Nobody was free to hunt it.',
  'chose.wild-animals.avoid': 'Foragers will keep close to home for {seasons} seasons.',
  'chose.raid.fight': 'The raiders were fought off; they took {food} food and {tools} tools.',
  'chose.raid.pay': 'The raiders were paid {food} food and went away.',
  'chose.raid.hide': 'Everyone hid; the raiders took {food} food and {tools} tools.',
  'chose.refugees.admit-all': 'You admitted them all.',
  'chose.refugees.admit-some': 'You admitted some of them.',
  'chose.refugees.guests': 'You took them in as guests.',
  'chose.refugees.turn-away': 'You turned them away.',
  'chose.travellers.admit-all': 'You admitted them.',
  'chose.travellers.admit-some': 'You admitted some of them.',
  'chose.travellers.guests': 'You took them in as guests.',
  'chose.travellers.turn-away': 'You turned them away.',
  'chose.trade-caravan.trade-first': 'Traded {giveAmount} {give} for {getAmount} {get}.',
  'chose.trade-caravan.trade-second': 'Traded {giveAmount} {give} for {getAmount} {get}.',
  'chose.trade-caravan.decline': 'The caravan moved on.',
  'chose.discovery.develop': 'Foragers went to develop the find.',
  'chose.discovery.develop.herb-patch': '{names} went to tend the herb patch.',
  'chose.discovery.develop.fish-run': '{names} went to build weirs on the fish run.',
  'chose.discovery.develop.clay-bed': '{names} went to clear the spring for a well.',
  'chose.discovery.develop.none': 'No forager was free to develop the find.',
  'chose.discovery.ignore': 'The find was left alone.',
  'chose.improvement-proposal.commit': 'Work on the improvement began.',
  'chose.improvement-proposal.commit.crop-rotation':
    '{name} will spend the season laying out a rotation for the fields.',
  'chose.improvement-proposal.commit.better-kiln':
    '{name} will spend the season building a better kiln.',
  'chose.improvement-proposal.commit.granary-lining':
    '{name} will spend the season lining the granary.',
  'chose.improvement-proposal.commit.none':
    'Nobody skilled enough was free; the idea was set aside.',
  'chose.improvement-proposal.decline': 'The proposal was declined.',
  'chose.dispute.side-a': "You sided with {winner}; {loser}'s household feels wronged.",
  'chose.dispute.side-b': "You sided with {winner}; {loser}'s household feels wronged.",
  'chose.dispute.mediate': 'You mediated; neither side is wholly satisfied.',
  'chose.elders-request.grant': 'You granted the request.',
  'chose.elders-request.grant.stop-working': '{name} has stopped working.',
  'chose.elders-request.grant.teach': '{name} has taken up teaching.',
  'chose.elders-request.grant.leave': '{name} will leave to live with kin.',
  'chose.elders-request.refuse': 'You refused {name}.',
  'chose.birth-complication.assign-healer': '{name} was called away to attend the birth.',
  'chose.birth-complication.assign-healer.none': 'There was no healer to send.',
  'chose.birth-complication.do-nothing': 'The mother and child were left to their own strength.',
  'chose.stranger.admit': 'The stranger was let in.',
  'chose.stranger.admit.healer': '{name} was let in, and proved a skilled healer.',
  'chose.stranger.admit.thief':
    '{name} was let in, and slipped away in the night with {food} food and {tools} tools.',
  'chose.stranger.refuse': 'The stranger was sent on.',
  'chose.rumour.address': 'You gathered the {people} who were wavering and heard them out.',
  'chose.rumour.let-go': 'You let them go; {people} will leave.',
  'preview.arrival-admit': '{people} more to feed and house; {beds} beds spare now.',
  'preview.arrival-admit-some': 'Choose who comes in; {beds} beds spare now.',
  'preview.arrival-guests': 'Half rations for {seasons} seasons, then decide again.',
  'preview.arrival-turn-away': 'Devout people ({devout}) lose trust and happiness.',
  'preview.arrival-turn-away-starving':
    'A starving family with children: everyone loses happiness, and the Ledger records it.',
  'preview.blight-burn': 'Lose {loss}% of the crop now; next year normal.',
  'preview.blight-harvest':
    'Harvest {mul}x, its seed worth {seed}x; {persist}% chance it returns next year.',
  'preview.drought-dig': '{builders} builders dig instead of building; water back to normal.',
  'preview.drought-ration': 'Water production stays at {mul}x.',
  'preview.quarantine':
    '{people} sick held apart for {seasons} seasons; {food} food for their keep.',
  'preview.carry-on': 'The sick keep working among the well, and it spreads.',
  'preview.close-gates': 'Newcomers are turned away for {seasons} seasons.',
  'preview.hunt': '{hunters} of {needed} hunters go: +{food} food, each {risk}% likely to be hurt.',
  'preview.avoid': 'Forage {mul}x for {seasons} seasons.',
  'preview.raid-fight':
    'About {loss}% of food and tools lost; {guards} {guards|guard|guards} at risk.',
  'preview.raid-pay': 'Give {food} food.',
  'preview.raid-hide': 'Lose {loss}% of food and tools; nobody hurt.',
  'preview.stranger-admit': 'A healer, or a thief: there is no telling yet.',
  'preview.trade': 'Give {giveAmount} {give} (you have {have}) for {getAmount} {get}.',
  'preview.discovery-herb-patch':
    '{foragers} foragers lose a season; then +1 medicine a season for {seasons} seasons.',
  'preview.discovery-fish-run':
    '{foragers} foragers lose a season; then +1 food per forager for {seasons} seasons.',
  'preview.discovery-clay-bed': '{foragers} foragers lose a season; then room for one more well.',
  'preview.improvement-commit': '{name} gives up a season of work; the gain is for good.',
  'preview.improvement-no-worker': 'Needs someone with {job} skill {skill}+, and nobody is free.',
  'preview.dispute-side': "{name}'s household ({people}) loses {trust} trust.",
  'preview.dispute-mediate': 'Both lose {trust} trust.',
  'preview.elder-grant-stop-working': 'They stop working.',
  'preview.elder-grant-teach': 'They become a teacher.',
  'preview.elder-grant-leave': 'They leave at the next Spring or Summer.',
  'preview.elder-refuse': 'Their trust {trust}, happiness {happiness}.',
  'preview.birth-healer': '{name} attends: the risks halve, and one fewer patient is treated.',
  'preview.birth-no-healer': 'No healer is at work.',
  'preview.birth-alone': 'Mother {mother}% and child {infant}% at risk.',
  'preview.rumour-address': '{food} food for a gathering; their trust +{trust}.',
  'preview.rumour-let-go': '{people} people leave.',
  'arrival.admit-all': 'All {size} were admitted.',
  'arrival.admit-some': '{admitted} of {size} were admitted; the rest were turned away.',
  'arrival.guests': '{size} were taken in as guests.',
  'arrival.turn-away': '{size} were turned away at the gate.',
  'departure.unhappy': '{names} left the settlement, unhappy here.',
  'departure.distrust': '{names} left: they no longer trust your leadership.',
  'departure.unsafe': '{names} left: they did not feel safe here.',
  'departure.hunger': '{names} left in search of food.',
  'departure.restless': '{names} left to see what lies beyond.',
  'departure.rumour': '{names} left for the better valley they had heard of.',
  'departure.exile': '{names} left in exile.',
  'departure.kin': '{names} left to live with kin elsewhere.',
  'departure.breakdown': '{names} left as the settlement broke apart.',
  'departure.turned-away': '{names} were sent on their way.',
  'departure-planned.unhappy': "{name}'s household is unhappy and will leave in {season}.",
  'departure-planned.distrust':
    "{name}'s household has lost faith in you and will leave in {season}.",
  'departure-planned.unsafe': "{name}'s household does not feel safe and will leave in {season}.",
  'departure-planned.hunger': "{name}'s household is hungry and will leave in {season}.",
  'departure-planned.restless': "{name}'s household is restless and will leave in {season}.",
  'departure-planned.rumour': "{name}'s household will leave for the better valley in {season}.",
  'departure-planned.exile': '{name} will be exiled in {season}.',
  'departure-planned.kin': '{name} will leave to live with kin in {season}.',
  'departure-planned.breakdown': "{name}'s household will leave in {season}.",
  'departure-planned.turned-away': '{name} must leave in {season}.',
  'improvement.crop-rotation': 'Crop rotation adopted: harvests are 10% better.',
  'improvement.better-kiln': 'A better kiln was built: building costs less.',
  'improvement.granary-lining': 'The granary was lined: stored food keeps better.',
};

export const narration: Record<NarrationTemplateId, string> = {
  ...eventNarration,
  death: '{name}, aged {age}, died of {cause}. {factors}',
  'death.illness': '{name}, aged {age}, died of {illness}. {factors}',
  birth: '{name} was born to {mother}.',
  production: '{job}s ({workers}) produced {amount} {resource}.',
  'production.service': '{job}s ({workers}) gave {amount} {resource}.',
  planted: '{plots} plots were planted with {seed} seed.',
  'seed.reserved': '{amount} food was set aside as seed for next Spring.',
  'seed.eaten': 'The seed corn was eaten: {amount}.',
  'seed.short': 'Seed ran short by {amount}.',
  shortage: '{resource} covered {covered} of {population} people; short by {shortfall}.',
  'rationing-outcome': '{resource} was rationed by the standing order. Short: {groups}.',
  'rationing-outcome.by-hand':
    '{resource} was rationed by hand. Short: {groups}. Given nothing: {none}.',
  spoilage: '{amount} {resource} spoiled.',
  'warning.food-short': 'Food is short by {amount}.',
  'warning.water-short': 'Water is short by {amount}.',
  'warning.fuel-short': 'Winter fuel short by {amount}.',
  'warning.no-toolmaker': 'No toolmaker for {seasons} seasons.',
  'warning.tools-short': '{amount} {amount|worker|workers} went without tools.',
  'warning.seed-not-reserved': 'Seed not reserved: {amount} short.',
  'warning.build-blocked': 'Construction of the {building} waits for {resource}: {amount} short.',
  'warning.build-limit': 'The {building} cannot be finished: its limit is reached.',
  'warning.upkeep-overdue':
    'Upkeep is overdue on {amount} {amount|building|buildings}; they are wearing down.',
  'warning.plot-lost': '{name} left the fields; what they planted is lost.',
  'warning.farmers-without-plots': '{amount} {amount|farmer|farmers} had no planted plot.',
  'warning.overcrowded': '{amount} {amount|household is|households are} overcrowded.',
  'warning.sleeping-rough': '{amount} people are sleeping rough.',
  'warning.job-vacant': 'A {job} post is vacant.',
  'warning.crops-look-thin': 'The crops look thin.',
  'warning.well-low': 'The well is lower than last Summer.',
  'warning.low-trust': 'Trust in your leadership is low.',
  illness: '{name} fell ill with {illness}.',
  recovery: '{name} recovered from {illness}.',
  injury: '{name} was injured at work.',
  'injury.assault': '{name} was hurt in an assault.',
  'injury.raid': '{name} was hurt fighting off raiders.',
  'injury.event': '{name} was injured.',
  treatment: '{name} was treated with medicine.',
  'treatment.no-medicine': '{name} was tended by a healer, but there was no medicine.',
  'passed-over': '{name} was passed over for treatment.',
  'quarantine.held': '{people} sick were kept in quarantine, at a cost of {food} food.',
  'quarantine.lifted': 'The quarantine was lifted.',
  'job-vacant': 'The {job} post held by {name} is vacant.',
  'came-of-age': '{name} came of age.',
  'became-elder': '{name} is now an elder.',
  'building-complete': 'A {building} was completed.',
  construction: 'Builders put {points} points into the {building}; {remaining} remain.',
  'build-cancelled': 'The {building} order was cancelled; {wood} wood and {stone} stone returned.',
  'building-damaged': 'A {building} was damaged: {amount} condition lost.',
  'building-repaired': 'The {building} was repaired.',
  'building-upkeep': 'Upkeep paid on the {building}: {wood} wood, {stone} stone.',
  'building-frozen': 'The {building} froze over; it will give nothing this Winter.',
  'building-thawed': 'The {building} thawed.',
  tools: 'Tools: {made} made, {repaired} repaired, {worn} worn away.',
  'knowledge-lost': 'Knowledge lost: {name} was the last to know {skill} well.',
  stunted: '{name} will carry the mark of a hungry childhood.',
  'policy-changed': 'Policy changed: {policy}.',
  // Sections 11, 15 and 16 (Stream D).
  'warning.children-uncovered':
    '{amount} small {amount|child has|children have} no one to mind them.',
  miscarriage: '{name} lost the child she was carrying.',
  pregnancy: '{name} is expecting a child.',
  pairing: '{a} and {b} have paired and set up a household.',
  'pairing.apart': '{a} and {b} have paired, but there are no beds for them together.',
  'moved-in': '{a} and {b} have found beds and set up a household together.',
  skill: '{name} reached skill {level} as a {job}.',
  literate: '{name} has learned to read and write.',
  school: '{students} children were taught by {teachers} teachers.',
  'school.waiting':
    '{students} children were taught by {teachers} teachers; {waiting} found no place.',
  'norm.established': 'A norm has taken hold: {norm}.',
  'norm.lost': 'A norm has faded: {norm}.',
  'norm.reinforced': 'The {norm} precedent was followed again.',
  'trust.up': 'Trust rose by {delta} for {people} people ({reason}).',
  'trust.down': 'Trust fell by {delta} for {people} people ({reason}).',
  festival: 'A festival was held in the common hall: {food} food and {wood} wood.',
  'festival-skipped.no-hall': 'No festival: there is no common hall to hold it.',
  'festival-skipped.short-food': 'No festival: there is not enough food in the stores.',
  'festival-skipped.short-wood': 'No festival: there is not enough wood.',
  refusal: 'Workers in {households} households refused to work next season.',
  // Section 13 (stream M4a): crimes, unexplained losses and justice.
  'crime.theft-food': '{offender} was caught stealing {amount} food from the stores.',
  'crime.theft-food.unexplained': 'Unexplained loss: {amount} food is missing from the stores.',
  'crime.theft-tools': '{offender} was caught stealing {amount} {amount|tool|tools}.',
  'crime.theft-tools.unexplained': 'Unexplained loss: {amount} {amount|tool is|tools are} missing.',
  'crime.assault': '{offender} assaulted {victim}.',
  'crime.assault.unexplained': '{victim} was attacked; no one saw by whom.',
  'crime.sabotage': '{offender} damaged the {building}.',
  'crime.sabotage.unexplained': 'Unexplained damage: the {building} lost {amount} condition.',
  'crime.killing': '{offender} killed {victim}.',
  'crime.killing.unexplained': '{victim} was found dead. No one knows who did it.',
  'justice.pardon': '{offender} was pardoned.',
  'justice.restitution': '{offender} must make restitution with extra work.',
  'justice.imprisonment': '{offender} was imprisoned.',
  'justice.flogging': '{offender} was flogged.',
  'justice.exile': '{offender} was exiled and will leave next season.',
  'justice.execution': '{offender} was executed.',
  event: 'Event: {event}.',
  'steady.pass': 'A steady year: {counter} of {target}.',
  'steady.fail': 'Not a steady year. Failed: {failed}.',
  'outcome.won': 'The settlement has held steady. You have won.',
  'outcome.lost': 'The settlement has failed: {reason}.',
  generic: '{text}',
};

export const seasonLabel: Record<Season, string> = {
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Winter',
};

export const resourceLabel: Record<Resource | 'seed' | 'warmth', string> = {
  food: 'Food',
  water: 'Water',
  wood: 'Wood',
  stone: 'Stone',
  tools: 'Tools',
  medicine: 'Medicine',
  cloth: 'Cloth',
  fibre: 'Fibre',
  seed: 'Seed',
  warmth: 'Firewood',
};

/** Labels for any quantity a narration may name (stocks and services). */
const quantityLabel: Partial<Record<string, string>> = {
  ...resourceLabel,
  buildPoints: 'build points',
  healingSlots: 'treatments',
  teachingSeats: 'school places',
  careSlots: 'care places',
  safetyPoints: 'safety points',
};

export const causeLabel: Record<DeathCause, string> = {
  starvation: 'starvation',
  dehydration: 'dehydration',
  exposure: 'exposure',
  illness: 'illness',
  childbirth: 'childbirth',
  infant: 'infancy',
  injury: 'injury',
  violence: 'violence',
  execution: 'execution',
  'old-age': 'old age',
  'wild-animal': 'a wild animal',
  raid: 'a raid',
};

export const text = {
  title: 'Utopia',
  tagline: 'A settlement ledger kept by a careful clerk.',
  newGame: 'Begin a new settlement',
  continue: 'Continue',
  start: 'Found the settlement',
  endSeason: 'End season',
  dismiss: 'Continue to the season',
  toTitle: 'Back to title',
  stable: (n: number, target: number): string => `Stable: ${n} of ${target} years`,
  noReport: 'Nothing to report yet.',
  errorTitle: 'Something went wrong. The last autosave was restored.',
};

/** Fill a narration template. Job ids and causes are rendered with their labels. */
/** Section 18 policy names for report lines (M6: the report said "foodRation"). */
export const policyLabel: Record<PolicyId, string> = {
  foodRation: 'Food ration',
  foodFloor: 'Food floor',
  foodPriority: 'Food priority order',
  seedReserve: 'Seed reserve',
  waterRation: 'Water ration',
  warmth: 'Warmth',
  healthCare: 'Health care',
  toolAllocation: 'Tool allocation',
  housing: 'Housing',
  announceRations: 'Announce rations',
  householdSharing: 'Household sharing',
  childLabour: 'Child labour',
  schooling: 'Schooling',
  schoolSubjects: 'School subjects',
  retirementAge: 'Retirement age',
  workIntensity: 'Work intensity',
  pairing: 'Pairing',
  migration: 'Migration',
  justice: 'Justice',
  guardMandate: 'Guard mandate',
  festival: 'Festival',
  burial: 'Burial',
  askOnShortage: 'Ask on shortage',
};

export function fill(n: Narration, jobLabel: (job: JobId) => string): string {
  // M7: `{key|one|many}` picks the singular or plural by the number in `key` ("1 household is").
  const template = narration[n.template].replace(
    /\{(\w+)\|([^|}]*)\|([^}]*)\}/g,
    (_match, key: string, one: string, many: string) => (Number(n.params[key]) === 1 ? one : many),
  );
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = n.params[key];
    if (value === undefined) return '';
    if (key === 'job' && typeof value === 'string' && value !== '')
      return jobLabel(value as JobId).toLowerCase();
    if (key === 'cause' && typeof value === 'string') return causeLabel[value as DeathCause];
    if (key === 'reason' && typeof value === 'string') return trustReasonLabel[value] ?? value;
    if (key === 'resource' && typeof value === 'string')
      return (quantityLabel[value] ?? value).toLowerCase();
    // Stream E2: trade offers name two resources (give1, get1, ...).
    if (/^(give|get)\d*$/.test(key) && typeof value === 'string')
      return (quantityLabel[value] ?? value).toLowerCase();
    if (key === 'policy' && typeof value === 'string' && value in policyLabel)
      return policyLabel[value as PolicyId].toLowerCase();
    if (key === 'building' && typeof value === 'string' && value in buildingLabel)
      return buildingLabel[value as BuildingTypeId].toLowerCase();
    if (key === 'groups' && typeof value === 'string')
      return value
        .split(',')
        .filter((g) => g !== '')
        .map((g) => ((priorityGroupLabel as Partial<Record<string, string>>)[g] ?? g).toLowerCase())
        .join(', ');
    return String(value);
  });
}

// ---- Stream B (M3a): Build tab, Tools panel and housing strings (Sections 9, 10, 18, 22).

export const buildingLabel: Record<BuildingTypeId, string> = {
  shelter: 'Shelter',
  house: 'House',
  field: 'Field',
  granary: 'Granary',
  well: 'Well',
  cistern: 'Cistern',
  woodshed: 'Woodshed',
  workshop: 'Workshop',
  clinic: 'Clinic',
  school: 'School',
  'watch-post': 'Watch post',
  palisade: 'Palisade',
  'common-hall': 'Common hall',
  cemetery: 'Cemetery',
  jail: 'Jail',
};

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

export const buildText = {
  buildingsTitle: 'Buildings',
  noBuildings: 'No buildings stand.',
  colBuilding: 'Building',
  colCondition: 'Condition',
  colEffect: 'Effect',
  colUpkeep: 'Upkeep (10% every 8 seasons)',
  colActions: 'Actions',
  frozen: 'frozen for the Winter',
  upkeepIn: (n: number): string =>
    n <= 0 ? 'due now' : `due in ${n} ${plural(n, 'season', 'seasons')}`,
  upkeepShort: 'overdue: short of materials',
  repair: 'Repair',
  repairQueued: 'Repair queued',
  repairOf: (label: string): string => `Repair ${label.toLowerCase()}`,
  fullCondition: 'In full repair',
  queueTitle: 'Build queue',
  queueEmpty: 'Nothing queued. Builders pour all their points into the first item.',
  forecast: (builders: number, points: string): string =>
    `${builders} ${plural(builders, 'builder', 'builders')}, ${points} build points a season`,
  noBuilders: 'No builders are assigned, so the queue will not move.',
  colPosition: 'Order',
  colProgress: 'Points',
  colMaterials: 'Materials (on hand / needed)',
  colSeasons: 'Seasons left',
  colStatus: 'Status',
  status: {
    building: 'building',
    next: 'starts next',
    waiting: 'waiting its turn',
    blocked: 'blocked: short of materials',
    paused: 'paused',
    'at-limit': 'waits: limit reached',
  },
  seasons: (n: number | null): string =>
    n === null ? 'not moving' : `${n} ${plural(n, 'season', 'seasons')}`,
  paid: 'paid',
  short: (n: string): string => `short ${n}`,
  pending: 'queued this season',
  moveUp: 'Up',
  moveDown: 'Down',
  moveHint: 'Alt and the arrow keys move the focused order.',
  moveUpLabel: (label: string): string => `Move ${label} up`,
  moveDownLabel: (label: string): string => `Move ${label} down`,
  pause: 'Pause',
  resume: 'Resume',
  cancel: 'Cancel',
  remove: 'Remove',
  cancelNoRefund: 'nothing paid yet',
  refund: (what: string): string => `returns ${what}`,
  cancelledTitle: 'Cancelled this season',
  undoCancel: 'Keep it',
  optionsTitle: 'Queue a building',
  colCost: 'Build points',
  colGives: 'Gives',
  colOwned: 'Owned / limit',
  noLimit: 'no limit',
  queueButton: 'Queue',
  limitReached: 'limit reached',
  beds: (n: number): string => `${n} beds`,
  plots: (n: number): string => `${n} plots`,
  capacity: (n: number, resource: string): string => `+${n} ${resource} store`,
  housingTitle: 'Housing',
  housingSummary: (beds: number, people: number): string => `${beds} beds for ${people} people`,
  rough: (n: number): string => `${n} ${plural(n, 'person', 'people')} sleeping rough`,
  crowded: (n: number): string =>
    `${n} ${plural(n, 'household', 'households')} overcrowded (happiness -5, illness 1.3x per extra person)`,
  housingMode: 'Beds go to',
  housingModes: {
    'families-first': 'Families first',
    'workers-first': 'Workers first',
    'first-come': 'First come',
    manual: 'Manual (keep homes as they are)',
  },
  allowOvercrowding: 'Allow overcrowding',
  splitHouseholds: 'Split a household across buildings when nothing else fits',
  explain: 'Why this number',
  base: 'base',
};

export const toolsText = {
  title: 'Tools',
  onHand: 'Tools on hand',
  effective: 'Effective after wear',
  durability: 'Mean durability',
  demand: "Wanted by next season's workers",
  shortfall: 'Short',
  noShortfall: 'enough for everyone',
  policy: 'Tools go to',
  policies: {
    'farmers-first': 'Farmers first',
    'highest-output': 'Highest output first',
    'builders-first': 'Builders first',
    'even-rotation': 'Even rotation',
    manual: 'Manual',
  },
  lastSeason: (made: string, repaired: string, worn: string): string =>
    `Last season: ${made} made, ${repaired} repaired, ${worn} worn away.`,
  noToolmaker: (n: number): string => `No toolmaker for ${n} ${plural(n, 'season', 'seasons')}.`,
  decaying: 'Idle tools are rusting: without a toolmaker the stock wears to nothing.',
  colWorker: 'Worker',
  colJob: 'Next season',
  colHadTool: 'Had a tool last season',
  colDifference: 'Output difference',
  colPin: 'Give a tool',
  yes: 'yes',
  no: 'no',
  notAtToolJob: 'not at a tool job',
  withTool: (n: string): string => `+${n} from the tool`,
  withoutTool: (n: string): string => `+${n} lost without one`,
  pinHint: 'Pins apply under the Manual policy.',
  noWorkers: 'Nobody works at a job that uses tools.',
};

// ---- Stream A (M2a/M2b): Rations tab and shortage dialog strings (Sections 17, 18, 22).

export const priorityGroupLabel: Record<PriorityGroupId, string> = {
  farmers: 'Farmers',
  'food-producers': 'Other food producers',
  'water-carriers': 'Water carriers',
  builders: 'Builders',
  toolmakers: 'Toolmakers',
  healers: 'Healers',
  teachers: 'Teachers',
  guards: 'Guards',
  'other-workers': 'Other workers',
  pregnant: 'Pregnant',
  'children-under-6': 'Children under 6',
  'children-6-14': 'Children 6 to 14',
  elders: 'Elders',
  'sick-or-injured': 'Sick or injured',
  newcomers: 'Newcomers',
  founders: 'Founders',
  'idle-adults': 'Idle adults',
  'my-household': 'My household',
  'everyone-else': 'Everyone else',
};

export const rationPolicyLabel: Record<RationPolicyId, string> = {
  equal: 'Equal shares',
  'floor-priority': 'Minimum floor, then priority',
  priority: 'Priority only',
  manual: 'Manual',
};

export const waterRationLabel: Record<WaterRationOption, string> = {
  equal: 'Equal',
  'sick-first': 'Sick first',
  'workers-first': 'Workers first',
  manual: 'Manual',
};

export const warmthLabel: Record<WarmthOption, string> = {
  equal: 'Equal',
  'children-first': 'Households with children first',
  'elders-first': 'Elders first',
  'workers-first': 'Workers first',
};

export const seedReserveLabel: Record<SeedReserveOption, string> = {
  'always-keep': "Always keep next Spring's seed",
  'keep-unless-starvation': 'Keep unless starvation (ask me)',
  'eat-freely': 'Eat freely',
};

export const rationResourceLabel: Record<RationResource, string> = {
  food: 'Food',
  water: 'Water',
  warmth: 'Firewood',
  medicine: 'Medicine',
};

const pct = (x: number): string => `${Math.round(x * 100)}%`;
const signed = (x: number, digits = 0): string =>
  `${x >= 0 ? '+' : '−'}${Math.abs(x).toFixed(digits)}`;

export const rationsText = {
  title: 'Rations',
  readOnly: 'Rations can be changed while you decide the season.',
  changed: 'changed this season',
  policiesHeading: 'Ration policies',
  foodPolicy: 'Food ration',
  waterPolicy: 'Water ration',
  warmthPolicy: 'Warmth',
  floor: 'Food floor',
  floorOption: (f: number): string => `${pct(f)} of need`,
  floorHint: 'Everyone gets the floor first under "Minimum floor, then priority".',
  orderHeading: 'Food priority order',
  orderHint:
    'Drag a group, or focus it and press the arrow keys, to rank it. Groups are fed in full in this order; the weakest of a group first.',
  moveUp: (group: string): string => `Move ${group} up`,
  moveDown: (group: string): string => `Move ${group} down`,
  remove: (group: string): string => `Remove ${group}`,
  grip: 'Drag to rank',
  members: (n: number): string => `${n} ${n === 1 ? 'person' : 'people'}`,
  addGroup: 'Add a group',
  addGroupChoose: 'Choose a group',
  add: 'Add',
  orderMoved: (group: string, place: number): string => `${group} moved to place ${place}.`,
  presetsHeading: 'Saved orders',
  presetName: 'Name',
  presetAnnounced: 'Announced',
  saveOrder: 'Save this order',
  applyOrder: 'Use',
  deleteOrder: 'Delete',
  announcedTag: 'announced',
  hiddenTag: 'hidden',
  noPresets: 'No saved orders yet.',
  announce: 'Announce the order',
  announceHint:
    'Announced: those rationed short lose half the trust (they knew the rule), but any deviation costs double. Turning this off costs trust 3.',
  seed: 'Seed reserve',
  sharing: 'Household sharing',
  sharingHint:
    'A fed member passes a quarter of their surplus to short members of their household.',
  ask: 'Ask on shortage',
  askHint: 'Off applies the policy silently and reports afterwards.',
  overridesHeading: 'Overrides',
  overridesHint: 'Pins are honoured before the policy fills the rest.',
  overridesFor: 'Pins for',
  warmthPinHint: 'Firewood is shared by household and these pins apply in Winter only.',
  peopleHeading: 'People',
  householdsHeading: 'Households',
  person: 'Person',
  age: 'Age',
  job: 'Job',
  household: 'Household',
  membersCol: 'Members',
  pin: 'Pin',
  myHousehold: 'My household',
  full: 'Full',
  half: 'Half',
  none: 'None',
  clear: 'Clear',
  pinned: (share: number): string =>
    share >= 1 ? 'pinned full' : share <= 0 ? 'pinned none' : `pinned ${pct(share)}`,
  householdName: (name: string): string => `${name}'s household`,
  previewHeading: 'Next season',
  previewNone: 'No shortage projected next season (assuming no events).',
  preview: (
    resource: string,
    covered: number,
    population: number,
    short: number,
    belowFloor: number,
  ): string =>
    `${resource}: ${covered} of ${population} fed in full; ${short} short, ${belowFloor} below the floor (assuming no events).`,
  previewAsk: 'The shortage dialog will ask you.',
};

export const shortageText = {
  title: 'Shortage',
  header: (resource: string, covered: number, population: number, season: string): string =>
    `${resource} covers ${covered} of ${population} people this ${season}.`,
  shortfall: (units: string, needed: string, available: string): string =>
    `Short by ${units} units (${available} on hand for ${needed} needed).`,
  policyNote: (policy: string, floor: number): string =>
    `Proposed by ${policy}; the floor is ${pct(floor)} of need.`,
  noTimer: 'There is no timer. The season waits for you.',
  stripHeading: 'If you confirm this (assuming no events)',
  stripFood: 'Food at the end of next season',
  stripFarmers: 'Farming group strength',
  stripDeaths: 'Expected deaths this season',
  stripTrust: 'Trust change',
  stripInequality: 'Inequality',
  recomputing: 'Recomputing…',
  none: 'none',
  farmers: (now: string, next: string): string => `${now} to ${next}`,
  trust: (x: number): string => signed(x, 1),
  deaths: (x: number): string => x.toFixed(1),
  food: (x: number, produced: number | null): string =>
    produced === null ? x.toFixed(1) : `${x.toFixed(1)} (grown ${produced.toFixed(1)})`,
  inequality: (x: number): string => x.toFixed(2),
  listHeading: 'Who receives what',
  colName: 'Name',
  colAge: 'Age',
  colJob: 'Job',
  colStrength: 'Strength',
  colShort: 'Short, last 4',
  colHousehold: 'Household',
  colShare: 'Share',
  colSet: 'Set',
  colPin: 'Pin',
  full: 'Full',
  half: 'Half',
  noneButton: 'None',
  setFor: (label: string, name: string): string => `${label} for ${name}`,
  pinFor: (name: string): string => `Keep ${name}'s setting as a standing pin`,
  share: (share: number): string =>
    share >= 1 - 1e-9 ? 'full' : share <= 1e-9 ? 'none' : pct(share),
  edited: 'edited',
  ifNone: (now: number, next: number, job: string, change: number | null): string =>
    change === null
      ? `If given nothing: strength ${Math.round(now)} to ${Math.round(next)}.`
      : `If given nothing: strength ${Math.round(now)} to ${Math.round(next)}, ${job} output next season about ${change <= 0 ? '−' : '+'}${Math.abs(Math.round(change * 100))}%.`,
  secondSeason: (now: number, next: number | null): string =>
    next === null
      ? 'Second season short: health starts falling.'
      : `Second season short: health starts falling (${Math.round(now)} to ${Math.round(next)}).`,
  hintPending: 'Working out what this ration means…',
  seedHeading: 'Seed corn',
  seedHint: (available: string): string =>
    `${available} food is set aside as seed for next Spring. Eating it is recorded in the Ledger as "ate the seed corn".`,
  seedLabel: 'Seed to eat',
  confirm: 'Confirm',
  reset: 'Reset to policy',
  equal: 'Apply equal shares',
  saveOrder: 'Save this as a priority order',
  saveName: 'Name for the order',
  saveDefault: 'Hungry season plan',
  saveCancel: "Don't save",
  saving: (name: string): string => `Will be saved as "${name}".`,
  edits: (n: number): string => `${n} ${n === 1 ? 'row' : 'rows'} changed by hand.`,
  escTitle: 'Discard your changes?',
  escBody: "Your edits will be lost and the policy's proposal restored.",
  escDiscard: 'Discard changes',
  escKeep: 'Keep editing',
};
// ---- End stream A.

// ---- Stream D (M3c, M4c): trust reasons, norms and the Society tab (Sections 11, 15, 16, 22).

/** Section 16 trust causes, as the report and tooltips name them. */
export const trustReasonLabel: Partial<Record<string, string>> = {
  'rationed-short': 'rationed short',
  'rationed-short-equal': 'rationed short under equal shares',
  'rationed-short-deviation': 'rationed short against the announced order',
  'child-rationed-short': 'a child rationed short',
  'fed-full-while-others-short': 'fed while others went short',
  'treated-short-medicine': 'treated when medicine was short',
  'passed-over-treatment': 'passed over for treatment',
  punished: 'punished',
  'watched-harsh-punishment': 'watched a harsh punishment',
  'given-tool': 'given a tool',
  'all-needs-met': 'every need met',
  'promised-build': 'a promised building finished',
  festival: 'the festival',
  'deprivation-death': 'a death from want',
  'deprivation-death-household': 'a death from want in the household',
  'policy-change': 'a policy change',
  'safety-short': 'feeling unsafe',
  idle: 'idle too long',
  charisma: 'word from a respected household',
  'norm-harsh-justice': 'troubled by harsh justice',
  departure: 'a neighbour left',
  'turned-away': 'people turned away',
};

/** Section 16 norms: name and effect once established. */
export const normText: Record<NormId, { label: string; effect: string }> = {
  'workers-first': {
    label: 'Workers first',
    effect: 'Non-workers expect short rations (trust penalties halved); children unhappier (-3).',
  },
  'children-first': {
    label: 'Children first',
    effect: 'Adults accept short rations at -1 trust instead of -8.',
  },
  'elders-last': {
    label: 'Elders last',
    effect: 'Elders unhappier (-5) and less likely to leave; elder teachers teach at 0.8x.',
  },
  'equal-shares': {
    label: 'Equal shares',
    effect: 'Any unequal ration now costs twice the trust.',
  },
  'founders-first': {
    label: 'Founders first',
    effect: 'Newcomers start with trust 25; fewer arrivals come (0.5x).',
  },
  'harsh-justice': {
    label: 'Harsh justice',
    effect: 'Deterrence 1.3x; Gentle and Devout people lose 1 trust a season.',
  },
  'merciful-justice': {
    label: 'Merciful justice',
    effect: 'Crime 1.2x; trust recovers 1.3x faster.',
  },
};

export const subjectLabel = (subject: string, jobLabel: (job: JobId) => string): string =>
  subject === 'literacy' ? 'Literacy' : jobLabel(subject as JobId);

export const societyText = {
  title: 'Society',
  trust: 'Settlement trust',
  happiness: 'Mean happiness',
  trustHint: (refusal: number, crime: number, bonus: number): string =>
    `Below ${crime} crime doubles and people start to leave; below ${refusal} workers may refuse; above ${bonus} output +5%.`,
  inequality: 'Inequality',
  inequalityFood: 'Food (Gini)',
  inequalityBeds: 'Beds (Gini)',
  inequalityHint: 'Gini of food received relative to need: 0 is equal, 1 is one person eats all.',
  norms: 'Norms',
  normsEmpty: 'No precedent has been set yet.',
  normEstablished: 'established',
  normProgress: (count: number, needed: number): string => `${count} of ${needed} in a row`,
  normAnnounced: 'announced',
  normHidden: 'not announced',
  normSince: (season: string): string => `since ${season}`,
  normFades: (n: number): string => `fades in ${n} ${n === 1 ? 'season' : 'seasons'} without use`,
  factions: 'Faction marks',
  factionsEmpty: 'No faction marks.',
  newcomers: (until: string): string => `Newcomers, until ${until}`,
  festival: 'Festival',
  festivalOff: 'No festival is planned (Policies: Festival).',
  festivalNext: (season: string): string => `Next festival: ${season}`,
  festivalActive: (seasons: number): string =>
    `Festival spirit lasts ${seasons} more ${seasons === 1 ? 'season' : 'seasons'}.`,
  festivalNoHall: 'Festivals need a common hall.',
  festivalLast: (season: string): string => `Last held ${season}`,
  festivalCost: (food: number, wood: number): string => `Costs ${food} food and ${wood} wood.`,
  pairings: 'Pairings awaiting approval',
  pairingsEmpty: 'None.',
  approve: 'Approve',
  refuse: 'Refuse',
  approved: 'approved',
  refused: 'refused',
  justice: 'Justice',
  ledger: 'Open the Ledger',
  education: 'Education',
  schooling: 'Schooling policy',
  subjects: 'Subjects this year',
  seats: 'School seats',
  teachers: 'Teachers',
  students: 'In school',
  waiting: 'Waiting for a place',
  literacy: 'Literacy',
  noSchool: 'No school stands: children cannot be taught.',
  apprenticeships: 'Apprenticeships',
  apprenticeshipsEmpty: 'No apprentices.',
  master: 'Master',
  apprentice: 'Apprentice',
  job: 'Job',
  active: 'learning',
  inactive: 'not learning (not working alongside)',
  end: 'End',
  addApprentice: 'Apprentice',
  chooseApprentice: 'Choose an adult',
  chooseMaster: 'Choose a master (skill 4+)',
  add: 'Set',
  refusal: (households: number): string =>
    `${households} ${households === 1 ? 'household refuses' : 'households refuse'} to work this season.`,
  uncovered: (n: number): string =>
    `${n} small ${n === 1 ? 'child has' : 'children have'} no carer: they are unhappier and sicker, and working parents lose 20% output.`,
  justiceLater: 'Justice cases appear here.',
  couples: 'Couples',
  couplesEmpty: 'No couples who could have a child.',
  colCouple: 'Couple',
  colConception: 'Chance of a child this season',
  colExpecting: 'Expecting',
  dueIn: (n: number): string => `due in ${n} ${n === 1 ? 'season' : 'seasons'}`,
  conceptionLabel: (name: string): string => `Chance ${name} conceives`,
  colSkills: 'Skill (apprentice / master)',
  colGain: 'Gain a season',
  colActions: 'Actions',
  gainLabel: (name: string): string => `Skill ${name} gains a season`,
  thisSeason: '(this season)',
  answer: 'Answer',
  inequalityPeak: 'Highest in the last 12 seasons',
  inequalityTitle: (value: string, max: number): string =>
    `Food inequality (Gini): ${value}; 0 is equal, ${max} or more very unequal`,
  inequalityShow: (value: string): string => `Inequality: ${value}. Show details.`,
  tipShow: (label: string, value: string): string => `${label}: ${value}. Show how it is made up.`,
  tipEmpty: 'Nothing moved it last season.',
  tipBase: 'base',
  sinceWhen: (season: string, year: number): string => `${season} year ${year}`,
  schoolingOption: {
    none: 'None',
    voluntary: 'Voluntary',
    'compulsory-12': 'Compulsory 6 to 12',
    'compulsory-14': 'Compulsory 6 to 14',
  } as Record<SchoolingOptionId, string>,
  subjectN: (n: number): string => `Subject ${n}`,
};

type SchoolingOptionId = 'none' | 'voluntary' | 'compulsory-12' | 'compulsory-14';

// ---- Stream F (Section 22 shell, tabs, overlays, settings; Section 24.12 saves). All strings
// under one object so other streams' additions never collide with these names.

/** Section 18 policy preview lines, keyed by PreviewLine.key ({param} placeholders). Policy
 * content owners add their keys here (see .context/requests.md). */
export const policyPreviewText: Record<string, string> = {};

export const uiText = {
  app: {
    seed: 'Seed',
    skipToMain: 'Skip to the work area',
    loading: 'Resolving the season…',
  },
  header: {
    season: 'Season',
    year: 'year',
    population: 'Population',
    fragility: 'Fragility',
    fragilityHint: 'Fragility, 0 to 100: the early-warning dial (Section 20). Lower is safer.',
    fragilityForecast: 'forecast',
    fragilityPending: 'forecasting the first season',
    steady: 'Steady state',
    stable: (n: number, target: number): string => `Stable: ${n} of ${target} years`,
    lastFailed: 'Last failed',
    neverChecked: 'Checked at the end of every Winter.',
    endSeason: 'End season',
    menu: 'Menu',
    observer: 'Observer',
    play: 'Play',
    pause: 'Pause',
    step: 'Step',
    ironman: 'Ironman',
    sandbox: 'Sandbox',
  },
  blocked: {
    title: 'No game in progress.',
    'reading-report': 'Read the season report first.',
    resolving: 'The season is resolving.',
    paused: 'The season is waiting on the shortage dialog.',
    ended: 'The settlement has ended.',
    unanswered: (n: number): string =>
      `${n} ${n === 1 ? 'question needs' : 'questions need'} an answer before the season can end.`,
  },
  confirm: {
    title: 'End the season?',
    nothing: 'Nothing was changed this season: everyone keeps their job and every policy stands.',
    changed: 'Changes this season',
    jobs: (n: number): string => `${n} ${n === 1 ? 'job change' : 'job changes'}`,
    policies: (n: number): string => `${n} ${n === 1 ? 'policy change' : 'policy changes'}`,
    builds: (n: number): string => `${n} ${n === 1 ? 'build order' : 'build orders'}`,
    renames: (n: number): string => `${n} ${n === 1 ? 'rename' : 'renames'}`,
    pins: (n: number): string => `${n} ration ${n === 1 ? 'pin' : 'pins'}`,
    answers: (n: number): string => `${n} ${n === 1 ? 'answer' : 'answers'}`,
    overrides: (n: number): string =>
      `${n} tool or treatment ${n === 1 ? 'override' : 'overrides'}`,
    move: (name: string, from: string, to: string): string => `${name}: ${from} to ${to}`,
    buildQueue: 'queue',
    buildCancel: 'cancel',
    buildPause: 'pause',
    buildMove: 'reorder',
    confirm: 'End season',
    cancel: 'Keep working',
  },
  menu: {
    save: 'Save…',
    load: 'Load…',
    exportSave: 'Export save',
    exportRun: 'Export run (shareable)',
    import: 'Import…',
    settings: 'Settings',
    howToPlay: 'How to play',
    ledger: 'Ledger',
    title: 'Title screen',
    close: 'Close menu',
  },
  tabs: {
    label: 'Work area',
    people: 'People',
    jobs: 'Jobs',
    rations: 'Rations',
    build: 'Build',
    policies: 'Policies',
    society: 'Society',
    health: 'Health',
    history: 'History',
    debug: 'Debug',
  },
  list: {
    title: 'People',
    filter: 'Show',
    all: 'Everyone',
    byHousehold: 'Household',
    byJob: 'Job',
    household: (name: string, size: number): string => `${name}'s household (${size})`,
    job: (label: string, count: number): string => `${label} (${count})`,
    count: (shown: number, total: number): string =>
      shown === total ? `${total} people` : `${shown} of ${total} people`,
    open: (name: string): string => `Open ${name}'s card`,
  },
  flags: {
    hungry: 'hungry',
    sick: 'sick',
    injured: 'injured',
    grieving: 'grieving',
    pregnant: 'pregnant',
    idle: 'idle',
    'in-school': 'in school',
    'in-custody': 'in custody',
    stunted: 'stunted',
    child: 'child',
    elder: 'elder',
  },
  ageGroup: { child: 'Child', adult: 'Adult', elder: 'Elder' },
  sex: { female: 'F', male: 'M' },
  sexLong: { female: 'female', male: 'male' },
  origin: { founder: 'Founder', born: 'Born here', newcomer: 'Newcomer' },
  stocks: {
    title: 'Stocks',
    capacity: (n: string): string => `capacity ${n}`,
    trend: 'Four seasons',
    trendUp: 'rising',
    trendDown: 'falling',
    trendFlat: 'steady',
    noTrend: 'no trend yet',
  },
  projection: {
    title: 'Next season',
    assumption: 'assuming no events',
    recomputing: 'recomputing…',
    unavailable: 'No projection while the season is not being decided.',
    failed: 'The projection could not be computed.',
    production: 'made',
    consumption: 'used',
    spoilage: 'spoiled',
    change: 'change',
    end: 'end',
    short: 'short',
    deaths: 'Expected deaths',
    fragility: 'Fragility',
  },
  warnings: {
    title: 'Warnings',
    none: 'No warnings.',
    lastSeason: 'last season',
    thisSeason: 'this season',
  },
  liveWarning: {
    'live.projected-short': 'Projected {resource} shortage next season.',
    'live.projected-short-later': 'Projected {resource} shortage in {season}.',
    'live.fuel-short': '{season} fuel short by {amount}.',
    'live.unanswered': 'Questions awaiting an answer: {count}.',
    'live.tools-worn':
      'Tools worn to {percent}%: they all break at 0 unless a toolmaker repairs them.',
    'live.idle-adults':
      '{count} {count|adult is|adults are} idle: after four seasons idle adults lose happiness and trust.',
    'live.over-capacity':
      'The {resource} store is {amount} over capacity: the excess spoils {mul}x as fast until more storage stands.',
  },
  people: {
    title: 'People',
    caption: 'Everyone in the settlement',
    name: 'Name',
    age: 'Age',
    sex: 'Sex',
    job: 'Job',
    strength: 'Str',
    health: 'Health',
    happiness: 'Happy',
    trust: 'Trust',
    skills: 'Top skills',
    household: 'Household',
    flags: 'Status',
    group: 'Group',
    groupAuto: 'Automatic',
    groupNone: 'No grouping',
    groupHousehold: 'By household',
    groupJob: 'By job',
    expand: (label: string): string => `Show ${label}`,
    collapse: (label: string): string => `Hide ${label}`,
    groupSummary: (count: number, flagged: number): string =>
      flagged === 0 ? `${count} people` : `${count} people, ${flagged} in need`,
    pending: '(from next season)',
    renamed: (to: string): string => `(to be ${to})`,
    sortedBy: (label: string, dir: 'asc' | 'desc'): string =>
      `Sorted by ${label}, ${dir === 'asc' ? 'ascending' : 'descending'}`,
  },
  person: {
    title: (name: string): string => name,
    close: 'Close',
    rename: 'Rename',
    renameLabel: 'New name',
    renameSave: 'Rename',
    renamePending: (to: string): string => `Will be known as ${to} from next season.`,
    age: (years: number): string => `${years} years`,
    stats: 'Condition',
    health: 'Health',
    strength: 'Strength',
    happiness: 'Happiness',
    trust: 'Trust',
    traits: 'Traits',
    noTraits: 'No marked traits.',
    status: 'Status',
    family: 'Family and household',
    noFamily: 'Alone.',
    relation: {
      partner: 'partner',
      parent: 'parent',
      child: 'child',
      household: 'household',
    },
    fate: { died: 'died', left: 'left', exiled: 'exiled' },
    skills: 'Skills',
    noSkills: 'No skills yet.',
    peak: (level: string): string => `peak ${level}`,
    memory: 'Memory',
    noMemory: 'Nothing recorded in the last eight seasons.',
    history: 'History',
    noHistory: 'Nothing in the kept reports.',
    whyUnhappy: 'Why is this person unhappy?',
    notUnhappy: 'Nothing is pulling their happiness down.',
    happinessTarget: (target: string, now: string): string =>
      `Happiness moves 40% a season toward ${target} (now ${now}).`,
    job: 'Job',
  },
  memory: {
    'fed-short': 'Fed short of {resource}.',
    'fed-none': 'Went without {resource}.',
    'fed-full-while-others-short': 'Fed in full while others went short.',
    thirsty: 'Went thirsty.',
    cold: 'Went cold.',
    'no-shelter': 'Slept without shelter.',
    punished: 'Was punished.',
    'saved-by-healer': 'Was saved by a healer.',
    'passed-over': 'Was passed over for treatment.',
    taught: 'Was taught.',
    'given-tool': 'Was given a tool.',
    promoted: 'Was given a skilled job.',
    bereaved: 'Lost {other}.',
    'child-fed-short': 'Their child went short.',
    'fell-ill': 'Fell ill.',
    injured: 'Was injured.',
    festival: 'Kept a festival.',
    arrived: 'Arrived at the settlement.',
    victim: 'Was the victim of a crime.',
  },
  traits: {
    hardy: 'Hardy',
    frail: 'Frail',
    diligent: 'Diligent',
    'quick-learner': 'Quick learner',
    devout: 'Devout',
    restless: 'Restless',
    fertile: 'Fertile',
    gentle: 'Gentle',
    volatile: 'Volatile',
    charismatic: 'Charismatic',
  } as Record<string, string>,
  jobs: {
    title: 'Jobs',
    hintNone:
      'Select a person (click, or Enter on their name), then choose a job; or drag them onto a column.',
    hintSelected: (name: string): string =>
      `Assigning ${name}: choose a job, or press Esc to cancel.`,
    assignHere: 'Assign here',
    assignTo: (name: string, job: string): string => `Assign ${name} to ${job}`,
    count: 'working',
    eligible: (n: number): string => `${n} eligible`,
    notEligible: 'Not eligible',
    vacant: (name: string, reason: string): string => `Vacant: ${name}'s post (${reason})`,
    vacantReason: { died: 'died', left: 'left', aged: 'too old now', unable: 'unable' } as Record<
      string,
      string
    >,
    needs: 'Needs next season',
    needLine: (label: string, needed: string, projected: string, onHand: string): string =>
      `${label}: ${needed} needed, ${projected} projected (${onHand} in store)`,
    projected: 'Projected output, next four seasons',
    service: 'a service; not stocked',
    baseline: 'dashed: with no changes this season',
    dip: (now: string, moved: string, skillNow: string, skillMoved: string): string =>
      `Here: productivity ${moved} (skill ${skillMoved}); now ${now} (skill ${skillNow}).`,
    dipFromRest: (moved: string, skillMoved: string): string =>
      `Here: productivity ${moved} (skill ${skillMoved}).`,
    losesPlot: 'Leaving the fields loses what this farmer planted.',
    autoFill: 'Auto-fill',
    autoFillHint:
      'Proposes jobs that cover needs using current skills. Nothing changes until you apply it.',
    autoFillTitle: 'Proposed changes',
    autoFillRow: (name: string, from: string, to: string): string => `${name}: ${from} to ${to}`,
    apply: 'Apply',
    discard: 'Discard',
    skill: 'skill',
    heavy: 'heavy',
    needsBuilding: (building: string): string => `needs a ${building}`,
    drop: (job: string): string => `Drop here to assign to ${job}`,
  },
  policies: {
    title: 'Policies',
    changed: (n: number): string =>
      `${n} ${n === 1 ? 'policy changes' : 'policies change'} next season.`,
    unchanged: 'No policy changes this season.',
    groups: {
      rations: 'Rations',
      health: 'Health',
      work: 'Work',
      housing: 'Housing',
      education: 'Education',
      society: 'Society',
      justice: 'Justice',
    } as Record<string, string>,
    standing: 'standing',
    pending: 'from next season',
    trustCost: (n: string): string => `Changing this costs ${n} trust for everyone.`,
    locked: 'locked',
    history: 'Changes',
    changedOn: (season: string, year: number, value: string): string =>
      `${season}, year ${year}: ${value}`,
    priorityNote: 'Edit the order in the Rations tab.',
    subjectsNote: 'Choose two subjects for this year.',
    overcrowding: 'Allow overcrowding',
    askEachCase: 'Ask each case',
    response: 'Response',
    mode: 'Mode',
    on: 'On',
    off: 'Off',
  },
  policyOption: {
    foodRation: {
      equal: 'Equal shares',
      'floor-priority': 'Minimum floor then priority',
      priority: 'Priority only',
      manual: 'Manual',
    },
    foodFloor: {
      '0.25': 'A quarter of need',
      '0.5': 'Half of need',
      '0.75': 'Three quarters of need',
    },
    seedReserve: {
      'always-keep': 'Always keep next Spring’s seed',
      'keep-unless-starvation': 'Keep unless starvation',
      'eat-freely': 'Eat freely',
    },
    waterRation: {
      equal: 'Equal',
      'sick-first': 'Sick first',
      'workers-first': 'Workers first',
      manual: 'Manual',
    },
    warmth: {
      equal: 'Equal',
      'children-first': 'Households with children first',
      'elders-first': 'Elders first',
      'workers-first': 'Workers first',
    },
    healthCare: {
      'most-likely-to-die': 'Most likely to die',
      'contagious-first': 'Contagious first',
      'workers-first': 'Workers first',
      'children-first': 'Children first',
      manual: 'Manual',
    },
    toolAllocation: {
      'farmers-first': 'Farmers first',
      'highest-output': 'Highest output',
      'builders-first': 'Builders first',
      'even-rotation': 'Even rotation',
      manual: 'Manual',
    },
    housing: {
      'families-first': 'Families first',
      'workers-first': 'Workers first',
      'first-come': 'First come',
      manual: 'Manual',
    },
    childLabour: {
      never: 'Never',
      emergencies: 'From 12 in emergencies',
      always: 'From 12 always',
    },
    schooling: {
      none: 'None',
      voluntary: 'Voluntary',
      'compulsory-12': 'Compulsory 6 to 12',
      'compulsory-14': 'Compulsory 6 to 14',
    },
    retirementAge: { '55': '55', '60': '60', '65': '65', never: 'Never' },
    workIntensity: { light: 'Light', normal: 'Normal', hard: 'Hard' },
    pairing: {
      free: 'Free',
      'min-18': 'Minimum age 18',
      'min-21': 'Minimum age 21',
      'player-approves': 'Player approves',
    },
    migration: {
      open: 'Open',
      'skilled-only': 'Skilled only',
      'families-only': 'Families only',
      closed: 'Closed',
      ask: 'Ask each time',
    },
    justice: {
      pardon: 'Pardon',
      restitution: 'Restitution',
      imprisonment: 'Imprisonment',
      flogging: 'Flogging',
      exile: 'Exile',
      execution: 'Execution',
    },
    guardMandate: {
      deter: 'Deter only',
      'patrol-stores': 'Patrol stores',
      'escort-foragers': 'Escort foragers',
    },
    festival: { off: 'Off', summer: 'Every Summer', autumn: 'Every Autumn' },
    burial: { cemetery: 'Cemetery', field: 'Field burial' },
  } as Record<string, Record<string, string>>,
  policyNote: {
    foodRation: 'How food is shared when it runs short (Section 17).',
    foodFloor: 'The share everyone gets first under the floor policy.',
    foodPriority: 'Who is fed first when food runs short.',
    seedReserve: '“Keep unless starvation” asks in the shortage dialog.',
    waterRation: 'How water is shared when it runs short.',
    warmth: 'Who is kept warm first when firewood runs short.',
    healthCare: 'Who the healers treat first.',
    toolAllocation: 'Who gets tools when there are fewer tools than workers.',
    housing: 'How beds are given out by household.',
    announceRations: 'Announced orders halve the trust penalty; deviations cost double.',
    householdSharing: 'A fed member shares 25% of their surplus with their household.',
    childLabour: '“Emergency” means any shortage last season.',
    schooling: 'Needs a school and a teacher.',
    schoolSubjects: 'Taught this year.',
    retirementAge: 'Elders past it are not auto-assigned; they may volunteer.',
    workIntensity:
      'Hard: output 1.15x, strength −8 a season, injuries 1.5x. Light: 0.85x, strength +5.',
    pairing: 'Section 15.',
    migration: 'Who may join the settlement.',
    justice: 'Execution needs a common hall and settlement trust of 60.',
    guardMandate: 'Escorts halve forager risk but deter theft less.',
    festival: 'Needs a common hall; costs 1 food a person and 2 wood.',
    burial: 'A cemetery halves grief.',
    askOnShortage: 'Off applies the policy silently and reports afterwards.',
  } as Record<string, string>,
  steady: {
    'no-deprivation-deaths': 'Nobody starved, froze or died of thirst',
    'needs-met': 'Needs met in 95% of person-seasons',
    'population-stable': 'Population stable',
    replacement: 'Births and children',
    'stocks-stable': 'Stocks not declining',
    'buildings-maintained': 'Buildings maintained',
    'social-floor': 'Trust and order',
    knowledge: 'Knowledge kept',
  } as Record<string, string>,
  namespace: {
    base: 'Base',
    season: 'Season',
    land: 'Land',
    need: 'Needs',
    age: 'Age and condition',
    illness: 'Illness',
    illnesses: 'Illness',
    contagion: 'Contagion',
    trait: 'Traits',
    skill: 'Skill',
    tool: 'Tools',
    tools: 'Tools',
    job: 'Job',
    building: 'Buildings',
    policy: 'Policies',
    norm: 'Norms',
    event: 'Events',
    fragility: 'Fragility terms',
    flow: 'Last season',
    seed: 'Seed',
    population: 'Last season',
  } as Record<string, string>,
  seed: {
    reserved: 'Set aside at harvest',
    eaten: 'Eaten',
    short: 'Short at planting',
    held: 'Held in store',
  } as Record<string, string>,
  flow: {
    start: 'Opening stock',
    production: 'Produced',
    consumption: 'Used',
    spoilage: 'Spoiled',
    theft: 'Stolen',
    other: 'Other',
  } as Record<string, string>,
  populationFlow: {
    start: 'At the start',
    births: 'Born',
    deaths: 'Died',
    arrivals: 'Arrived',
    departures: 'Left',
  } as Record<string, string>,
  tooltip: {
    result: 'Result',
    none: 'No factors.',
    explain: (label: string): string => `${label}: show where this number comes from`,
  },
  report: {
    title: (season: string, year: number): string => `Report: ${season}, year ${year}`,
    population: (start: number, end: number): string => `Population ${start} to ${end}.`,
    births: 'Births',
    born: (name: string, mother: string): string => `${name} was born to ${mother}.`,
    deaths: 'Deaths',
    death: (name: string, age: number, cause: string, job: string): string =>
      `${name}, aged ${age}, ${job}, died of ${cause}.`,
    factors: 'Contributing',
    health: 'Illness and recovery',
    crime: 'Crime and justice',
    events: 'Events',
    prompts: 'Questions for this season',
    answer: 'Answer',
    answered: 'answered',
    promptKind: {
      event: 'Event',
      justice: 'Justice case',
      pairing: 'Pairing',
      shortage: 'Shortage',
    } as Record<string, string>,
    production: 'Production',
    productionRow: 'this season',
    productionBefore: 'last season',
    shortages: 'Shortages',
    shortageLine: (
      resource: string,
      covered: number,
      population: number,
      shortfall: string,
    ): string => `${resource} covered ${covered} of ${population} people; short by ${shortfall}.`,
    whoShort: 'Went short',
    warnings: 'Warnings',
    steady: 'The year',
    steadyPass: (counter: number, target: number): string =>
      `A steady year: ${counter} of ${target}.`,
    steadyFail: 'Not a steady year. Failed:',
    outcomeWon: 'The settlement has held steady. You have won.',
    outcomeLost: 'The settlement has failed.',
    lossReason: {
      'population-collapse': 'too few people remain',
      'no-fertile-adults': 'no one remains who can have children',
      breakdown: 'trust collapsed and the settlement broke apart',
      'steady-state': 'the steady state held',
    } as Record<string, string>,
    other: 'The season',
    nothing: 'Nothing else to report.',
    continue: 'Continue to the season',
    ledger: 'Open the Ledger',
    toTitle: 'Back to title',
  },
  history: {
    title: 'History',
    charts: 'Over the run',
    empty: 'Nothing has happened yet: end a season to begin the record.',
    metric: {
      population: 'Population',
      food: 'Food',
      water: 'Water',
      wood: 'Wood',
      tools: 'Tools',
      trust: 'Trust',
      happiness: 'Happiness',
      fragility: 'Fragility',
      gini: 'Inequality (food Gini)',
    } as Record<string, string>,
    markers: 'Markers: ◆ events, ✝ deaths',
    marker: {
      event: (n: number, labels: string): string =>
        labels === '' ? `${n} ${n === 1 ? 'event' : 'events'}` : `Events: ${labels}`,
      death: (n: number, labels: string): string =>
        labels === '' ? `${n} ${n === 1 ? 'death' : 'deaths'}` : `Died: ${labels}`,
    },
    archive: 'Reports',
    summary: (population: number, births: number, deaths: number, events: number): string =>
      `Population ${population}; ${births} born, ${deaths} died; ${events} events.`,
    fragility: 'Fragility',
    productionFactors: 'Production factors',
    show: 'Show',
    hide: 'Hide',
    chartLabel: (metric: string, first: string, last: string, min: string, max: string): string =>
      `${metric}: from ${first} to ${last}; lowest ${min}, highest ${max}.`,
  },
  title: {
    newGame: 'Begin a new settlement',
    presets: 'Difficulty',
    preset: { gentle: 'Gentle', standard: 'Standard', unforgiving: 'Unforgiving' } as Record<
      string,
      string
    >,
    presetNote: {
      gentle: 'Fertile land, calm years, no raiders, a generous store, a skilled crew.',
      standard: 'The settlement as the Ledger expects it.',
      unforgiving: 'Harsh land and frequent raiders: frozen wells, thin harvests, long Winters.',
    } as Record<string, string>,
    options: 'Options',
    population: 'Starting population',
    seed: 'Seed (blank for random)',
    land: 'Land',
    disasters: 'Disaster frequency',
    threat: 'Threat level',
    stock: 'Starting stock',
    knowledge: 'Starting knowledge',
    target: 'Steady-state target',
    cadence: 'Turn cadence',
    cadenceValue: 'One season per turn',
    ironman: 'Ironman (one rolling save; earlier autosaves cannot be loaded)',
    observer: 'Observer mode (the steward plays; you watch)',
    start: 'Found the settlement',
    continue: 'Continue',
    continueDetail: (season: string, year: number, seed: string): string =>
      `${season}, year ${year} (seed ${seed})`,
    load: 'Load save',
    import: 'Import run',
    importHint: 'A Utopia save or shared run (.json).',
    settings: 'Settings',
    howToPlay: 'How to play',
    choices: {
      land: { fertile: 'Fertile', temperate: 'Temperate', harsh: 'Harsh' },
      disasters: { calm: 'Calm', normal: 'Normal', volatile: 'Volatile' },
      threat: { none: 'None', low: 'Low', high: 'High' },
      stock: {
        lean: 'Lean (1 season)',
        standard: 'Standard (2 seasons)',
        generous: 'Generous (4 seasons)',
      },
      knowledge: { novice: 'Novice', mixed: 'Mixed', skilled: 'Skilled' },
      target: { '5': '5 years', '10': '10 years', '20': '20 years' },
    } as Record<string, Record<string, string>>,
  },
  saves: {
    title: 'Saves',
    save: 'Save this game',
    saveName: 'Name',
    saveButton: 'Save',
    ironman: 'Ironman keeps one rolling save; named saves are off.',
    slots: 'Saved games',
    noSlots: 'No saved games.',
    autosaves: 'Autosaves',
    noAutosaves: 'No autosaves yet.',
    start: 'Start of run',
    load: 'Load',
    delete: 'Delete',
    confirmDelete: (name: string): string => `Delete “${name}”? This cannot be undone.`,
    yesDelete: 'Yes, delete',
    keep: 'Keep',
    row: (season: string, year: number, turn: number): string =>
      `${season}, year ${year} (turn ${turn})`,
    people: (n: number): string => `${n} people`,
    current: 'this run',
    ironmanTag: 'ironman',
    won: 'won',
    lost: 'lost',
    export: 'Export',
    exportSave: 'Export save',
    exportRun: 'Export run (shareable, no snapshot)',
    import: 'Import from a file',
    close: 'Close',
    unnamed: 'Untitled',
  },
  notice: {
    quota:
      'Browser storage is full: autosaves were cut to {detail}. Export the run to keep a copy.',
    saved: 'Saved “{detail}”.',
    'save-failed': 'Could not save “{detail}”: browser storage is full. Export the run instead.',
    loaded: 'Loaded.',
    'load-failed': 'That save could not be read.',
    imported: 'Imported the run (seed {detail}).',
    'import-failed': 'That file is not a Utopia save: {detail}',
    exported: 'Exported {detail}.',
    'ironman-no-slots': 'Ironman keeps one rolling save; named saves are off.',
    'storage-unavailable':
      'Browser storage is unavailable: settings and saves last only for this visit.',
    'autofill-empty': 'Auto-fill has nothing to change: current jobs already cover the plan.',
    forked: 'The run was replayed on the current balance.',
    dismiss: 'Dismiss',
  } as Record<string, string>,
  olderBalance: {
    title: 'Played on an older balance',
    config: 'This save was played with different game constants than this version uses.',
    content: 'The game content has changed since this save was made.',
    keep: 'Continue on the saved balance',
    fork: 'Fork onto the current balance',
  },
  error: {
    title: 'Something went wrong. The last autosave was restored.',
    render: 'Something went wrong drawing the screen.',
    copy: 'Copy diagnostics',
    copied: 'Copied.',
    show: 'Show diagnostics',
    dismiss: 'Dismiss',
    reload: 'Back to title',
    code: 'Code',
  },
  settings: {
    title: 'Settings',
    theme: 'Theme',
    themes: {
      system: 'Follow the system',
      light: 'Light',
      dark: 'Dark',
      'high-contrast': 'High contrast',
    } as Record<string, string>,
    textSize: 'Text size',
    textSizes: {
      small: 'Small (14 px)',
      normal: 'Normal (15 px)',
      large: 'Large (17 px)',
    } as Record<string, string>,
    motion: 'Motion',
    motions: {
      system: 'Follow the system',
      reduce: 'Reduce motion',
      full: 'Allow motion',
    } as Record<string, string>,
    autosaves: 'Autosaves kept',
    autosavesHint: 'The last N seasons, plus the start of the run.',
    confirmEnd: 'Confirm before ending a season',
    close: 'Close',
  },
  howToPlay: {
    title: 'How to play',
    close: 'Close',
    sections: [
      {
        heading: 'The aim',
        body: 'Hold the settlement steady for the target number of years. The counter in the header shows how close you are; a year that fails any condition resets it. There is no score for size.',
      },
      {
        heading: 'A season',
        body: 'Each turn is a season. Read the report, make your changes in any tab, then press End season (or Enter). The season resolves at once, unless a shortage asks you to choose who goes without.',
      },
      {
        heading: 'Jobs and delay',
        body: 'Food is decided in Spring and paid in Autumn. A person moving job works at their skill in the new job, usually low: the Jobs tab shows next four seasons’ output so you can see the dip before you cause it. Auto-fill proposes a plan and waits for you.',
      },
      {
        heading: 'Rations and policy',
        body: 'Policies are standing rules that apply every season until changed. When stock cannot cover everyone, the ration policy decides; the shortage dialog lets you choose by name. Repeated choices harden into norms.',
      },
      {
        heading: 'Reading the numbers',
        body: 'Hover or focus any underlined number to see where it comes from. The right panel projects next season assuming no events, and lists warnings. Fragility is the early-warning dial.',
      },
      {
        heading: 'Saves',
        body: 'The game autosaves at the start of every season and keeps the last few. Save to a named slot, export a run to a file, or import one from the menu. Ironman keeps a single rolling save.',
      },
      {
        heading: 'Keyboard',
        body: 'Tab moves between panels and controls; Enter ends the season when nothing else has focus; Esc closes an overlay (the shortage dialog asks before discarding). Arrow keys move between tabs.',
      },
    ],
  },
  debug: {
    title: 'Debug (development builds only)',
    rng: 'Random streams',
    main: 'main',
    events: 'events',
    cursor: 'Phase cursor',
    pending: 'Pausing prompts',
    open: 'Open prompts',
    none: 'none',
    runSteward: 'Run the steward for',
    seasons: 'seasons',
    run: 'Run',
    fastForward: 'Fast forward to the first shortage',
    fastForwardResult: (found: boolean): string =>
      found ? 'Paused on a shortage.' : 'No shortage reached.',
    state: 'State',
    key: 'Key',
    whole: '(whole state)',
    copyDiagnostics: 'Copy diagnostics',
  },
};

// ---- Stream C (M3b): Health tab strings (Sections 12, 18, 19, 22).
// Aliased so these never collide with another stream's imports of the same names.
import type {
  CareOutcome as HealthCareOutcome,
  HealthCareOption as HealthCarePolicyOption,
  IllnessId as HealthIllnessId,
} from '../../sim/api';

export const illnessLabel: Record<HealthIllnessId, string> = {
  fever: 'Fever',
  flux: 'Flux',
  injury: 'Injury',
  wasting: 'Wasting',
  plague: 'Plague',
};

export const careLabel: Record<HealthCareOutcome, string> = {
  medicine: 'Treated with medicine',
  'no-medicine': 'Tended, no medicine',
  'passed-over': 'Passed over',
  untreated: 'No healer',
  visit: 'Visited while well',
};

export const healthCareLabel: Record<HealthCarePolicyOption, string> = {
  'most-likely-to-die': 'Most likely to die first',
  'contagious-first': 'Contagious first',
  'workers-first': 'Workers first',
  'children-first': 'Children first',
  manual: 'Manual (mark by name)',
};

/** Labels for the illness multiplier's rows, by modifier source. */
const illnessFactorLabel: Partial<Record<string, string>> = {
  base: 'Base chance',
  'season:spring': 'Spring',
  'season:summer': 'Summer',
  'season:autumn': 'Autumn',
  'season:winter': 'Winter',
  'need:food': 'Hungry',
  'need:water': 'Dehydrated',
  'need:warmth': 'Cold',
  'need:shelter': 'Shelter',
  'trait:frail': 'Frail',
  'building:well': 'Well',
  'building:clinic': 'Clinic',
  'illness:contagion': 'Contagion',
  'water:sick-carrier': 'Sick water carriers',
  'migration:sick-arrival': 'Sick arrival',
  'care:uncovered': 'Small children not minded',
  'job:healer': 'Healer',
  'illness:fever': 'Fever',
  'illness:plague': 'Plague',
  'illness:injury': 'Injury',
};

export function illnessFactorName(source: string): string {
  const known = illnessFactorLabel[source];
  if (known !== undefined) return known;
  const colon = source.indexOf(':');
  const id = colon < 0 ? source : source.slice(colon + 1);
  return id.charAt(0).toUpperCase() + id.slice(1).replace(/-/g, ' ');
}

export const healthText = {
  title: 'Health',
  intro:
    'Illness is the second killer after hunger. Sick people do not work; untreated illness lasts longer and can spread.',
  patients: 'Sick and injured',
  noPatients: 'Nobody is sick or injured.',
  name: 'Name',
  illness: 'Illness',
  seasonsLeft: 'Seasons left',
  lastSeason: 'Last season',
  nextSeason: 'Next season',
  deathRisk: 'Death risk',
  mark: 'Mark',
  rank: 'Order',
  injuredToo: '+ injured',
  contagious: 'contagious',
  quarantined: 'in quarantine',
  treatedCourse: 'on the treated course',
  untreatedRisk: (pct: string): string => `${pct} untreated`,
  plannedRisk: (pct: string): string => `${pct} as planned`,
  treatFirst: 'Treat first',
  doNotTreat: 'Do not treat',
  clearMark: 'Clear',
  markChanged: '(changed this season)',
  policy: 'Health care policy',
  policyHint:
    'Who gets healer time and medicine when there is not enough for everyone. Marks by name come first under any policy.',
  pending: '(changes next season)',
  healers: 'Healers',
  noHealers: 'No healers are assigned. The sick go untreated.',
  slots: (slots: string, capacity: number): string =>
    `${slots} treatment slots: ${capacity} patient${capacity === 1 ? '' : 's'} a season`,
  notWorking: 'not working',
  treatedLast: (names: string): string => `treated ${names} last season`,
  visits: (n: number): string =>
    `Spare time: ${n} healthy ${n === 1 ? 'person' : 'people'} visited (+8 health instead of +4).`,
  medicine: 'Medicine',
  medicineLine: (onHand: string, capacity: string, demand: string): string =>
    `${onHand} on hand of ${capacity}; the sick need ${demand} a season.`,
  medicineShort: 'Short of medicine: the untreated keep their full death chance.',
  medicineUse: (use: string): string => `${use} will be used next season as planned.`,
  multiplier: 'Illness multiplier',
  multiplierAsRolled: (turn: number): string => `as rolled in season ${turn}`,
  multiplierNow: 'on the current state',
  multiplierHint:
    'Each person risks falling ill every season: 3% times these factors. A well and clean water carriers cut it most.',
  levers: 'Levers',
  lever: {
    well: 'A well halves illness from fouled water (x0.5).',
    clinic: 'A clinic cuts contagion (x0.7) and doubles healer capacity.',
    'sick-carrier': 'Sick water carriers foul the water (x1.5): rest them until well.',
    crowding: 'Crowded households fall ill more (x1.3 per extra person).',
    contagion: 'The sick spread illness to everyone (1 + 0.4 x share sick).',
    quarantine: 'A quarantine isolates the contagious sick (1 food each a season).',
  },
  leverGood: 'in place',
  leverMissing: 'missing',
  leverActive: 'in effect',
  leverClear: 'none',
  quarantine: 'Quarantine',
  quarantineOn: (isolated: number, until: string): string =>
    `In force${until}: ${isolated} isolated, costing 1 food each a season.`,
  quarantineUntil: (turn: number): string => ` until season ${turn}`,
  quarantineOff: 'No quarantine. An outbreak event offers one.',
  arrivalBoost: (turn: number): string => `A sick arrival raises contagion until season ${turn}.`,
  recent: 'Last season',
  noRecent: 'No illness, injury or recovery last season.',
  factorsFor: (what: string): string => `Why: ${what}`,
  base: 'Base',
  readOnly: 'Changes can be made while planning a season.',
};

// Section 18 policy preview lines for the health-care policy (keys from content/policies/health-care.ts).
Object.assign(policyPreviewText, {
  'policy.healthCare.patients':
    '{patients} sick or injured now; healers saw {capacity} last season.',
  'policy.healthCare.first.contagious-first': '{count} of them are contagious and are seen first.',
  'policy.healthCare.first.workers-first': '{count} of them are workers and are seen first.',
  'policy.healthCare.first.children-first': '{count} of them are children and are seen first.',
  'policy.healthCare.first.manual': '{count} marked by name are seen first; the rest by risk.',
});
// ---- End stream C.

// ---- Stream G (M5b): the Ledger screen, its tags and the character sketch (Section 21).

/**
 * Character sketch templates (Section 21), in a neutral clerk's voice. `{key}` is a parameter;
 * `{key|one|many}` picks the singular or plural by the number in `key`; `{norm}` is a norm id
 * rendered with `ledgerNormLabel`.
 */
export const sketchText: Record<SketchTemplateId, string> = {
  'sketch.outcome.won':
    'The settlement held steady for {target} years and was judged sound in year {year}.',
  'sketch.outcome.sandbox':
    'It held steady for {target} years in year {year} and played on; the count now stands at {counter}.',
  'sketch.outcome.lost.population-collapse':
    'It ended in year {year}, when too few people remained to carry on.',
  'sketch.outcome.lost.no-fertile-adults':
    'It ended in year {year}, with no one left to raise another generation.',
  'sketch.outcome.lost.breakdown':
    'It ended in year {year}, when trust broke down and households left en masse.',
  'sketch.outcome.ongoing':
    'The ledger stands open in year {year}, after {seasons} {seasons|season|seasons}.',
  'sketch.people':
    '{births} {births|child was|children were} born and {deaths} {deaths|person|people} died; the population peaked at {peak}.',
  'sketch.deprivation.some': '{count} {count|person|people} died of hunger, thirst or cold.',
  'sketch.deprivation.none': 'Nobody died of hunger, thirst or cold.',
  'sketch.short.none': 'Nobody was fed short.',
  'sketch.short.most.child':
    'Children went short more often than anyone else, in {percent}% of their seasons.',
  'sketch.short.most.adult':
    'Working-age adults went short more often than anyone else, in {percent}% of their seasons.',
  'sketch.short.most.elder':
    'Elders went short more often than anyone else, in {percent}% of their seasons.',
  'sketch.short.children-never': 'Children were never fed short.',
  'sketch.gate.none-came': 'No strangers came to the gate.',
  'sketch.gate.none-turned-away': 'Nobody was turned away at the gate.',
  'sketch.gate.turned-away':
    '{count} of {total} {total|group|groups} at the gate {count|was|were} turned away.',
  'sketch.gate.starving':
    '{count} starving {count|family was|families were} turned away at the gate.',
  'sketch.justice.none': 'No crime came before you for judgement.',
  'sketch.justice.executed': '{count} {count|person was|people were} executed.',
  'sketch.justice.exiled': '{count} {count|person was|people were} sent into exile.',
  'sketch.justice.flogged.all-left':
    '{count} {count|person was|people were} flogged; {count|they|all of them} left within a year.',
  'sketch.justice.flogged.some-left':
    '{count} {count|person was|people were} flogged; {left} of them left within a year.',
  'sketch.justice.flogged.stayed':
    '{count} {count|person was|people were} flogged, and {count|they|all of them} stayed.',
  'sketch.justice.lenient':
    '{count} {count|case was|cases were} judged without the lash, the rope or exile.',
  'sketch.seed.kept-hungry':
    'You kept the seed corn through {winters} hungry {winters|winter|winters}.',
  'sketch.seed.kept': 'The seed corn was never eaten.',
  'sketch.seed.eaten': 'The seed corn was eaten in {count} {count|season|seasons}.',
  'sketch.chosen.by-hand': 'You overrode the ration policy by hand {count} {count|time|times}.',
  'sketch.chosen.by-hand-none':
    'You overrode the ration policy by hand {count} {count|time|times}; {none} {none|person was|people were} given nothing.',
  'sketch.chosen.policy': 'Every shortage was settled by the standing policy.',
  'sketch.norm.established': 'The custom of {norm} took hold in year {year}.',
  'sketch.norm.established-announced': 'The announced custom of {norm} took hold in year {year}.',
  'sketch.norm.none': 'No custom hardened into a norm.',
  'sketch.festival.hungry': '{count} {count|festival was|festivals were} held in a hungry year.',
  'sketch.festival.held': '{count} {count|festival was|festivals were} held.',
  'sketch.child-labour': 'Children worked in {count} {count|season|seasons}.',
  'sketch.knowledge.lost':
    'Knowledge of {count} {count|craft|crafts} died with the last person who knew it well.',
  'sketch.knowledge.literacy': '{percent}% of the settlement could read at the end.',
  'sketch.knowledge.improvements': '{count} {count|improvement was|improvements were} adopted.',
  'sketch.inequality.peak': 'Food inequality peaked at {gini} in year {year}.',
  'sketch.inequality.even': 'Food was shared almost evenly throughout.',
  'sketch.fragility.peak': 'The settlement was most fragile in year {year}, at {value} of 100.',
  'sketch.founders-club': 'Newcomers lived shorter lives than founders.',
  'sketch.departures': '{count} {count|household|households} left of their own accord.',
  'sketch.youngest-death': 'The youngest to die was {name}, at {age} {age|year|years} old.',
  'sketch.youngest-death.infant': 'The youngest to die was {name}, in infancy.',
};

export const ledgerNormLabel: Record<NormId, string> = {
  'workers-first': 'workers first',
  'children-first': 'children first',
  'elders-last': 'elders last',
  'equal-shares': 'equal shares',
  'founders-first': 'founders first',
  'harsh-justice': 'harsh justice',
  'merciful-justice': 'merciful justice',
};

/** Fill a template with `{key}` and `{key|one|many}` placeholders. Unknown keys stay visible. */
export function fillTemplate(
  template: string,
  params: Record<string, string | number>,
  labels: Partial<Record<string, (value: string) => string>> = {},
): string {
  return template.replace(
    /\{(\w+)(?:\|([^|}]*)\|([^}]*))?\}/g,
    (match, key: string, one?: string, many?: string) => {
      const value = params[key];
      if (value === undefined) return match;
      if (one !== undefined && many !== undefined) return Number(value) === 1 ? one : many;
      const label = labels[key];
      return label === undefined ? String(value) : label(String(value));
    },
  );
}

/** One sketch line as prose. */
export function fillSketch(line: SketchLine): string {
  const template = sketchText[line.template as SketchTemplateId] as string | undefined;
  return fillTemplate(template ?? line.template, line.params, {
    norm: (id) => ledgerNormLabel[id as NormId],
  });
}

export const tagLabel: Record<LedgerTag, string> = {
  egalitarian: 'Egalitarian',
  utilitarian: 'Utilitarian',
  protective: 'Protective',
  austere: 'Austere',
  'open-gate': 'Open gate',
  'closed-gate': 'Closed gate',
  'hard-hand': 'Hard hand',
  forgiving: 'Forgiving',
  'founders-club': "Founders' club",
  learned: 'Learned',
};

export const tagMeaning: Record<LedgerTag, string> = {
  egalitarian: 'Mean food inequality under 0.08 across the run.',
  utilitarian: 'Workers were fed first, and nobody starved.',
  protective: 'Children were never fed short and never worked.',
  austere: 'The seed was always kept and no festival was held.',
  'open-gate': 'Admitted 80% or more of those who came.',
  'closed-gate': 'Turned away 80% or more of those who came.',
  'hard-hand': 'Harsh justice became the custom.',
  forgiving: 'Merciful justice became the custom.',
  'founders-club': "Newcomers' lives were 10% or more shorter than founders'.",
  learned: '60% or more could read at the end.',
};

export const ledgerSectionLabel = {
  outcome: 'Outcome',
  people: 'The people',
  wentWithout: 'Who went without',
  chosen: 'Who was chosen',
  norms: 'Norms',
  justice: 'Justice',
  gate: 'The gate',
  inequality: 'Inequality',
  knowledge: 'Knowledge',
  fragility: 'Fragility',
  sketch: 'Character sketch',
} as const;

export const ageGroupPlural: Record<AgeGroup, string> = {
  child: 'Children',
  adult: 'Working-age adults',
  elder: 'Elders',
};

export const originLabel: Record<Origin, string> = {
  founder: 'Founders',
  born: 'Born here',
  newcomer: 'Newcomers',
};

export const fateLabel: Record<LedgerFate, string> = {
  alive: 'Living',
  died: 'Died',
  left: 'Left',
  exiled: 'Exiled',
};

export const justiceResponseLabel: Record<JusticeResponse, string> = {
  pardon: 'Pardon',
  restitution: 'Restitution',
  imprisonment: 'Imprisonment',
  flogging: 'Flogging',
  exile: 'Exile',
  execution: 'Execution',
};

export const crimeLabel: Record<CrimeKind, string> = {
  'theft-food': 'Theft of food',
  'theft-tools': 'Theft of tools',
  assault: 'Assault',
  sabotage: 'Sabotage',
  killing: 'Killing',
};

export const arrivalDecisionLabel: Record<ArrivalDecision, string> = {
  'admit-all': 'Admitted all',
  'admit-some': 'Admitted some',
  guests: 'Admitted as guests',
  'turn-away': 'Turned away',
};

export const arrivalReasonLabel: Record<ArrivalReason, string> = {
  famine: 'fleeing famine',
  raiders: 'fleeing raiders',
  work: 'seeking work',
  exiled: 'exiled elsewhere',
  stranger: 'a stranger with a story',
};

export const departureReasonLabel: Partial<Record<EmigrationReason, string>> = {
  unhappy: 'unhappy',
  distrust: 'distrust',
  unsafe: 'unsafe',
  hunger: 'hunger',
  restless: 'restless',
  rumour: 'a rumour of a better valley',
  exile: 'exile',
  kin: 'following kin',
  breakdown: 'breakdown',
};

export const improvementLabel: Record<ImprovementId, string> = {
  'crop-rotation': 'Crop rotation',
  'better-kiln': 'A better kiln',
  'granary-lining': 'Granary lining',
};

export const steadyConditionLabel: Record<SteadyCondition, string> = {
  'no-deprivation-deaths': 'Nobody starved or died of exposure or thirst',
  'needs-met': 'Needs met',
  'population-stable': 'Population stable',
  replacement: 'Replacement',
  'stocks-stable': 'Stocks not declining',
  'buildings-maintained': 'Buildings maintained',
  'social-floor': 'Social floor',
  knowledge: 'Knowledge',
};

export const ledgerText = {
  title: 'The Ledger',
  won: 'Won: the settlement held steady',
  lost: 'Lost: the settlement failed',
  ongoing: 'The ledger is open.',
  lossReason: {
    'population-collapse': 'too few people remained',
    'no-fertile-adults': 'no one could raise another generation',
    breakdown: 'trust broke down',
  },
  seed: 'Seed',
  copySeed: 'Copy seed',
  options: 'Options',
  ironman: 'Ironman',
  sandbox: 'Sandbox',
  seasons: 'Seasons',
  steadyCount: (n: number, target: number): string => `Steady years: ${n} of ${target}`,
  tags: 'Tags',
  noTags: 'No tag fits this run.',
  score: 'At what cost',
  scoreLine: (s: {
    seasonsSurvived: number;
    steadyYears: number;
    rationingDeaths: number;
    corporalPunishments: number;
    executions: number;
    turnedAwayStarvingFamilies: number;
  }): string =>
    `${s.seasonsSurvived} seasons survived; ${s.steadyYears} steady years. ` +
    `Deaths from rationing: ${s.rationingDeaths}. Corporal punishments: ${s.corporalPunishments}. ` +
    `Executions: ${s.executions}. Starving families turned away: ${s.turnedAwayStarvingFamilies}.`,
  continueSandbox: 'Continue in sandbox',
  exportRun: 'Export run',
  copySummary: 'Copy summary',
  copied: 'Copied.',
  back: 'Back',
  sections: 'Ledger sections',
  none: 'None.',
  // The people
  births: 'Births',
  deaths: 'Deaths',
  deathsByCause: 'Deaths by cause',
  peak: 'Peak population',
  final: 'Final population',
  lifeExpectancy: 'Life expectancy (years, observed)',
  meanAgeAtDeath: 'Mean age at death (years)',
  oldest: 'Oldest living',
  youngestDeath: 'Youngest death',
  // Who went without
  shortLine: (parts: string[]): string => `${parts.join('; ')}.`,
  shortPart: (group: string, percent: number, first: boolean): string =>
    first
      ? `${group} were fed short in ${percent}% of person-seasons`
      : `${group.toLowerCase()} in ${percent}%`,
  name: 'Name',
  group: 'Group',
  origin: 'Origin',
  seasonsShort: 'Seasons short',
  seasonsNone: 'Seasons fed nothing',
  fate: 'Fate',
  // Who was chosen
  season: 'Season',
  resource: 'Resource',
  shortfall: 'Shortfall',
  pinnedNone: 'Given nothing',
  byHand: 'By hand',
  yes: 'yes',
  no: 'no',
  // Norms
  norm: 'Norm',
  change: 'Change',
  announced: 'Announced',
  normChange: { established: 'established', lost: 'lost' },
  policyChanges: 'Policy changes',
  policy: 'Policy',
  value: 'Set to',
  // Justice
  crime: 'Crime',
  offender: 'Offender',
  response: 'Response',
  reoffended: 'Reoffended',
  pending: 'pending',
  after: (n: number): string => `after ${n} ${n === 1 ? 'season' : 'seasons'}`,
  // The gate
  arrivals: 'Arrivals',
  size: 'People',
  reason: 'Reason',
  decision: 'Decision',
  admitted: 'Admitted',
  admittedShare: 'Admitted',
  turnedAwayShare: 'Turned away',
  groupFates: 'Founders, born here and newcomers',
  people: 'People',
  alive: 'Living',
  died: 'Died',
  left: 'Left',
  meanShort: 'Seasons short (mean)',
  trustAtEnd: 'Trust at end',
  departures: 'Departures',
  // Inequality, knowledge, fragility
  giniChart: 'Food inequality (Gini) by season',
  giniPeak: (peak: string, year: number): string => `Peak ${peak} in year ${year}`,
  giniMean: (mean: string): string => `Mean ${mean}`,
  literacyChart: 'Literacy by season',
  skillsLost: 'Skills lost on death',
  skill: 'Skill',
  level: 'Level',
  improvements: 'Improvements adopted',
  improvement: 'Improvement',
  finalLiteracy: (percent: number): string => `${percent}% literate at the end`,
  fragilityChart: 'Fragility index by season (markers: events)',
  fragilityPeak: (value: number, year: number): string => `Most fragile: ${value} in year ${year}`,
  events: (n: number): string => `${n} ${n === 1 ? 'event' : 'events'}`,
  when: (season: string, year: number): string => `${season}, year ${year}`,
  summaryHeading: 'Utopia run',
};

export const skillLabelForLedger = (skill: SkillId, jobLabel: (job: JobId) => string): string =>
  jobLabel(skill);

/** The new-game options as one line for the Ledger banner and the copied summary (Section 3). */
export function ledgerOptionsLine(o: {
  population: number;
  land: string;
  disasterFrequency: string;
  threatLevel: string;
  startingStock: string;
  startingKnowledge: string;
  steadyTargetYears: number;
}): string {
  const cap = (x: string): string =>
    x.length === 0 ? x : `${x[0]?.toUpperCase() ?? ''}${x.slice(1)}`;
  return [
    `${o.population} people`,
    `${cap(o.land)} land`,
    `${cap(o.disasterFrequency)} disasters`,
    `${cap(o.threatLevel)} threat`,
    `${cap(o.startingStock)} stock`,
    `${cap(o.startingKnowledge)} knowledge`,
    `${o.steadyTargetYears}-year target`,
  ].join(', ');
}

/** Section 19 event names, for the fragility chart's markers. */
export const ledgerEventLabel: Record<LedgerEventId, string> = {
  'poor-harvest': 'Poor harvest',
  blight: 'Blight',
  drought: 'Drought',
  'harsh-winter': 'Harsh winter',
  'early-frost': 'Early frost',
  fire: 'Fire',
  'fever-outbreak': 'Fever outbreak',
  plague: 'Plague',
  'wild-animals': 'Wild animals',
  raid: 'Raid',
  refugees: 'Refugees',
  travellers: 'Travellers seeking work',
  'trade-caravan': 'Trade caravan',
  'good-harvest': 'Good harvest',
  'mild-winter': 'Mild winter',
  discovery: 'Discovery',
  'improvement-proposal': 'Improvement proposal',
  dispute: 'Dispute',
  'elders-request': "Elder's request",
  'birth-complication': 'Birth complication',
  stranger: 'Stranger with a story',
  rumour: 'Rumour of a better valley',
};

export const ledgerChartText = {
  showTable: 'Show as a table',
  season: 'Season',
  value: 'Value',
  events: 'Events',
  year: (y: number): string => `Y${y}`,
  point: (when: string, value: string): string => `${when}: ${value}`,
  summary: (title: string, n: number, min: string, max: string, last: string): string =>
    `${title}: ${n} seasons, from ${min} to ${max}, last ${last}.`,
  noData: 'No seasons recorded yet.',
};
// ---- End stream G.

// ---- Stream E1 (M4a): the Justice panel and the case screen (Sections 13, 18, 22).
const guardsWord = (n: number): string => (n === 1 ? 'guard' : 'guards');

export const justiceText = {
  title: 'Justice',
  safetyTitle: 'Safety',
  safetyPoints: 'Safety points',
  safetyNeeded: (n: string): string => `Needed: ${n} (one for every 15 people).`,
  safetyMet: 'The safety need is met.',
  safetyShort: (pct: string): string => `The safety need is ${pct} met.`,
  undetectedKilling: 'An unsolved killing in the last four seasons leaves everyone afraid.',
  raidLoss: 'A raid took more than a tenth of the stores in the last four seasons.',
  guardsOnDuty: (n: number): string => `${n} ${guardsWord(n)} on duty.`,
  deterrence: 'Deterrence',
  crimeFactor: 'Crime chance',
  crimeFactorHint: 'Everyone’s chance of crime is divided by deterrence.',
  policyTitle: 'Justice policy',
  policyHint: 'Applied to each detected crime, unless you are asked about each case.',
  askEachCase: 'Ask me about each case',
  changesNextSeason: 'changes this coming season',
  mandateTitle: 'Guard mandate',
  mandates: {
    deter: 'Deter only',
    'patrol-stores': 'Patrol stores',
    'escort-foragers': 'Escort foragers',
  } satisfies Record<GuardMandateOption, string>,
  mandateHints: {
    deter: 'Guards stand watch: violence is deterred more, but they do not help catch thieves.',
    'patrol-stores': 'Guards patrol the stores: each one helps catch thieves.',
    'escort-foragers': 'Guards go out with foragers: forager risk halves, theft is deterred less.',
  } satisfies Record<GuardMandateOption, string>,
  responses: {
    pardon: 'Pardon',
    restitution: 'Restitution',
    imprisonment: 'Imprisonment',
    flogging: 'Flogging',
    exile: 'Exile',
    execution: 'Execution',
  } satisfies Record<JusticeResponse, string>,
  responseHints: {
    pardon: 'Cheap. The offender trusts you more; victims and everyone else less. Erodes safety.',
    restitution: 'The offender works extra for four seasons at reduced output.',
    imprisonment: 'Held from work for 2 to 4 seasons, still fed. Needs a free jail cell.',
    flogging: 'Strength and health lost. Others watch. Recorded as corporal punishment.',
    exile: 'The offender leaves for good; family may follow. Recorded.',
    execution: 'Death. Others watch; the devout take it hardest. Recorded.',
  } satisfies Record<JusticeResponse, string>,
  blocks: {
    'no-jail': 'Needs a jail.',
    'jail-full': 'Every jail cell is taken.',
    locked: 'Needs a common hall and settlement trust of 60.',
    'not-offered': 'Was not open to this case when it was brought.',
  } satisfies Record<JusticeBlock | 'not-offered', string>,
  crimes: {
    'theft-food': 'Theft of food',
    'theft-tools': 'Theft of tools',
    assault: 'Assault',
    sabotage: 'Sabotage',
    killing: 'Killing',
  } satisfies Record<CrimeKind, string>,
  pendingTitle: 'Cases awaiting judgement',
  noPending: 'No case awaits judgement.',
  resolvedTitle: 'Cases judged',
  noResolved: 'No case has been judged yet.',
  showing: (n: number, total: number): string => `The latest ${n} of ${total}.`,
  lossesTitle: 'Unexplained losses last season',
  noLosses: 'Nothing went missing that could not be explained.',
  stolen: (food: string, tools: string): string =>
    `Stolen last season, caught or not: ${food} food, ${tools} tools.`,
  loss: {
    'theft-food': (amount: string): string => `${amount} food missing from the stores`,
    'theft-tools': (amount: string): string => `${amount} tools missing`,
    assault: (victim: string): string => `${victim} attacked by someone unknown`,
    sabotage: (building: string): string => `the ${building} damaged by someone unknown`,
    killing: (victim: string): string => `${victim} killed by someone unknown`,
  } satisfies Record<CrimeKind, (x: string) => string>,
  caseTitle: (crime: string, name: string): string => `${crime}: ${name}`,
  when: (season: string, year: number): string => `${season}, year ${year}`,
  columns: {
    when: 'When',
    crime: 'Crime',
    offender: 'Offender',
    victims: 'Victims',
    response: 'Response',
    reoffended: 'Reoffended',
    fate: 'Now',
  },
  none: 'none',
  notJudged: 'not judged',
  yes: 'yes',
  no: 'no',
  fates: {
    here: 'here',
    leaving: 'leaving in exile',
    custody: 'in custody',
    died: 'dead',
    left: 'left',
    exiled: 'exiled',
  },
  proposed: 'the policy',
  judgement: 'Judgement',
  offenderGone: 'The offender is no longer here; the case will close unjudged.',
  facts: {
    age: 'Age',
    job: 'Job',
    trust: 'Trust',
    happiness: 'Happiness',
    health: 'Health',
    strength: 'Strength',
    hunger: 'Hunger',
    offences: 'Detected offences',
    crimeChance: 'Chance of crime',
  },
  fedFull: 'Fed in full last season.',
  fedShort: (pct: string, seasons: number): string =>
    `Fed ${pct} of need last season; short ${seasons} ${seasons === 1 ? 'season' : 'seasons'} running.`,
  family: 'Family',
  noFamily: 'No family in the settlement.',
  relations: { partner: 'partner', parent: 'parent', child: 'child', household: 'household' },
  away: 'gone',
  history: 'What was done to them',
  noHistory: 'Nothing notable in the last eight seasons.',
  memory: {
    'fed-short': 'fed short',
    'fed-none': 'given no food',
    'fed-full-while-others-short': 'fed in full while others went short',
    thirsty: 'short of water',
    cold: 'short of firewood',
    'no-shelter': 'slept rough',
    punished: 'punished',
    'saved-by-healer': 'saved by a healer',
    'passed-over': 'passed over for treatment',
    taught: 'taught',
    'given-tool': 'given a tool',
    promoted: 'promoted',
    bereaved: 'bereaved',
    'child-fed-short': 'their child was fed short',
    'fell-ill': 'fell ill',
    injured: 'injured',
    festival: 'at a festival',
    arrived: 'arrived',
    victim: 'a victim of crime',
  } as Partial<Record<MemoryKind, string>>,
  priorCases: 'Earlier cases',
};
// ---- End stream E1.

// ---- Stream B (M3a): policy preview lines (after policyPreviewText is declared).
Object.assign(policyPreviewText, {
  'policy.toolAllocation.stock':
    '{tools} tools on hand for {workers} workers who use one; {short} go without.',
  'policy.toolAllocation.farmers-first': '{count} farmers get a tool first.',
  'policy.toolAllocation.builders-first': '{count} builders get a tool first.',
  'policy.toolAllocation.highest-output': 'Tools go where they add the most output this season.',
  'policy.toolAllocation.even-rotation': 'Tools pass round the workers season by season.',
  'policy.toolAllocation.manual': '{count} workers marked by name get a tool.',
  'policy.housing.beds': '{beds} beds for {people} people.',
  'policy.housing.overcrowd':
    '{extra} people share beds (happiness -5, illness 1.3x per extra person).',
  'policy.housing.rough': 'At least {extra} people sleep rough.',
  'policy.housing.split': 'A household that fits nowhere whole is split across buildings.',
});

// ---- Stream E2 (M4b, M4d): the event prompt and arrival screen (Sections 14, 19, 22).
export const eventsText = {
  title: 'Questions for this season',
  progress: (answered: number, total: number): string => `${answered} of ${total} answered.`,
  none: 'Nothing is waiting on you.',
  answered: 'Chosen',
  notYet: 'Not yet answered.',
  readOnly: 'Answers can be given only while you decide the season.',
  done: 'Done',
  gateCaption: 'The people at the gate',
  guestsCaption: 'The guests',
  columns: {
    pick: 'Admit',
    name: 'Name',
    age: 'Age',
    health: 'Health',
    claims: 'Says they can',
    skillsShown: 'Has shown',
    sick: 'Looks',
  },
  child: 'child',
  noClaims: 'nothing in particular',
  looksSick: 'sick',
  looksWell: 'well',
  pickSome: (n: number): string =>
    n === 0 ? 'Tick the people to admit, then choose Admit some.' : `${n} ticked for Admit some.`,
  pickNone: 'Tick at least one person first.',
  migration: 'Migration policy:',
  migrationOption: {
    open: 'Open',
    'skilled-only': 'Skilled only',
    'families-only': 'Families only',
    closed: 'Closed',
    ask: 'Ask each time',
  } satisfies Record<MigrationOption, string>,
  migrationHint:
    'Any choice but Ask each time decides at the gate without asking, from next season.',
  gatesClosed: (turn: number): string => `The gates are closed through turn ${turn}.`,
};

// Section 18 migration policy previews (stream E2).
Object.assign(policyPreviewText, {
  'policy.migration.open': 'Everyone who comes to the gate is admitted without asking.',
  'policy.migration.skilled-only':
    'Only those claiming skill 3+ in a trade nobody here has at 3+ are admitted, with their children.',
  'policy.migration.families-only': 'Groups with children are admitted; others are turned away.',
  'policy.migration.closed': 'Everyone who comes to the gate is turned away.',
  'policy.migration.ask': 'You decide on each group at the gate.',
  'policy.migration.guests': '{count} guests are waiting to be decided on.',
  'policy.migration.gates-closed': 'The gates are closed through turn {turn} whatever the policy.',
});

// ---- Stream A (M2a/M2b): ration policy previews (after policyPreviewText is declared).
// Section 18 "affects next season" preview lines for the ration policies (policyPreview).
Object.assign(policyPreviewText, {
  'policy.foodRation.equal': 'If food runs short, all {people} people get the same share of need.',
  'policy.foodRation.floor-priority':
    'If food runs short, everyone gets {floor}% of need first; then {group} ({count}) are fed in full first.',
  'policy.foodRation.priority':
    'If food runs short, {group} ({count}) are fed in full first; the last groups may get nothing.',
  'policy.foodRation.manual':
    'If food runs short, the shortage dialog asks you who eats (all {people} people are listed).',
  'policy.foodFloor.applies':
    'Everyone gets {floor}% of need before anyone is fed in full ({people} people).',
  'policy.foodFloor.unused':
    'Used by "Minimum floor, then priority"; the shortage dialog also asks when someone falls below {floor}% of need.',
  'policy.foodPriority.order': '{first} ({firstCount}) are fed first; {last} ({lastCount}) last.',
  'policy.seedReserve.always-keep': 'The {seed} food set aside as seed for Spring is never eaten.',
  'policy.seedReserve.keep-unless-starvation':
    'The {seed} food set aside as seed is offered in the shortage dialog and eaten only if you choose.',
  'policy.seedReserve.eat-freely':
    'The {seed} food set aside as seed is eaten before anyone goes short.',
  'policy.waterRation.equal': 'If water runs short, all {people} people get the same share.',
  'policy.waterRation.sick-first': '{count} sick or injured get water first.',
  'policy.waterRation.workers-first':
    '{count} workers get water first; the rest share what is left.',
  'policy.waterRation.manual': 'If water runs short, the shortage dialog asks you who drinks.',
  'policy.warmth.equal': 'If firewood runs short, all {households} households get the same share.',
  'policy.warmth.children-first':
    '{count} of {households} households, those with children, are kept warm first.',
  'policy.warmth.elders-first':
    '{count} of {households} households, those with elders, are kept warm first.',
  'policy.warmth.workers-first':
    '{count} of {households} households, those with workers, are kept warm first.',
  'policy.announceRations.on':
    'Those rationed short lose {short} trust instead of {hidden}; a deviation from the order costs {deviation}.',
  'policy.announceRations.off':
    'Those rationed short lose {hidden} trust; no deviation costs extra.',
  'policy.householdSharing.on':
    'In {households} households a fed member passes {pct}% of their surplus to those who are short.',
  'policy.householdSharing.off': 'Each person keeps their own ration.',
  'policy.askOnShortage.on':
    'The season pauses on the shortage dialog when someone would fall below {floor}% of need.',
  'policy.askOnShortage.off':
    'Shortages are resolved by the policy without asking and reported afterwards.',
});
// ---- End stream A previews.

// ---- Stream D (M3c, M4c): policy preview lines (after policyPreviewText is declared).
Object.assign(policyPreviewText, {
  'policy.schooling.children': '{children} children are of school age (6 to 14).',
  'policy.schooling.pulled': 'Compulsory schooling pulls {children} children from work.',
  'policy.festival.cost': 'Each {season}: {food} food and {wood} wood; happiness +10, trust +4.',
  'policy.retirement.retired': '{people} people are {age} or older and would not be assigned work.',
});

// ---- M6: preview lines for the policies that had none (Section 18 "affect-preview line").
Object.assign(policyPreviewText, {
  'policy.childLabour.never':
    '{children} {children|child|children} aged {from} to 14 {children|stays|stay} out of work.',
  'policy.childLabour.emergencies':
    '{children} {children|child|children} aged {from} to 14 may be put to work in a season after a shortage.',
  'policy.childLabour.always':
    '{children} {children|child|children} aged {from} to 14 may be assigned work.',
  'policy.schoolSubjects.subjects':
    'This year the school teaches {first} and {second} to {students} {students|child|children} of school age.',
  'policy.schoolSubjects.no-school':
    'The subjects ({first} and {second}) are taught once a school stands; {students} {students|child is|children are} of school age.',
  'policy.schooling.none':
    'No schooling: {children} {children|child|children} of school age learn only by doing.',
  'policy.retirement.never': 'Nobody retires; {people} {people|elder|elders} may be assigned work.',
  'policy.festival.off': 'No festival is held.',
  'policy.workIntensity.normal': '{workers} people at work keep their usual pace.',
  'policy.workIntensity.light':
    'Output {output}x; the {workers} people at work regain {strength} strength a season.',
  'policy.workIntensity.hard':
    'Output {output}x; the {workers} people at work lose {strength} strength a season and are injured {injury}x as often.',
  'policy.pairing.age':
    'People pair from age {age}; {unpaired} unpaired {unpaired|person is|people are} that old.',
  'policy.pairing.approve':
    'Every pairing (from age {age}) waits for your approval on the Society tab; {pending} waiting now.',
  'policy.justice.response.pardon': 'Detected offenders are pardoned (deterrence {deterrence}x).',
  'policy.justice.response.restitution':
    'Detected offenders work off restitution (deterrence {deterrence}x).',
  'policy.justice.response.imprisonment':
    'Detected offenders are imprisoned (deterrence {deterrence}x).',
  'policy.justice.response.flogging': 'Detected offenders are flogged (deterrence {deterrence}x).',
  'policy.justice.response.exile': 'Detected offenders are exiled (deterrence {deterrence}x).',
  'policy.justice.response.execution':
    'Detected offenders are executed (deterrence {deterrence}x).',
  'policy.justice.ask': 'You judge each case yourself; {pending} waiting now.',
  'policy.justice.auto': 'Cases are settled by the policy without asking.',
  'policy.guardMandate.deter':
    '{guards} {guards|guard stands|guards stand} watch: violent-crime deterrence {violent}x, no patrols of the stores.',
  'policy.guardMandate.patrol-stores':
    '{guards} guards patrol the stores: theft detection +{detection}% per guard.',
  'policy.guardMandate.escort-foragers':
    '{guards} guards escort foragers: forager risk {risk}x, theft deterrence {theft}x.',
  'policy.burial.cemetery':
    'Burial in the cemetery: grief lasts {seasons} season(s); a death costs {happiness} happiness.',
  'policy.burial.field':
    'Field burial: grief lasts {seasons} seasons; a death costs {happiness} happiness.',
});
